import permissionMixin from "@/mixins/permissions-mixin.js";
import ProductServices from "@/services/Product";
import CategoryServices from "@/services/Category";
import Api from "@/services/Api";
import { financaBulkFailureText, financaServerLabel } from "@/utils/financaSaveNotice";

/** Max products in one scheduled Cmimet batch. Apliko tani is unlimited (chunked). */
export const PRICE_SCHEDULE_ITEM_CAP = 2000;
/** Chunk size for Oferta F5 Riprovo (matches backend OfferCampaignEngine::RESYNC_CHUNK_SIZE). */
export const OFFER_RESYNC_CHUNK_SIZE = 5;

export default {
  mixins: [permissionMixin],
  data() {
    return {
      pricingScreen: "cmimet",
      activeTab: "cmimet",
      allProducts: [],
      categoryTrees: {
        products: [],
        accessories: [],
      },
      form: {
        apply_mode: "bulk",
        related_entity: "products",
        target_field: "price",
        scope: "all",
        leaf_guid: "",
        discount_type: "percentage",
        amount: 0,
        selected_product_ids: [],
      },
      customRules: {},
      offerFushatList: [],
      offerFushatGuid: "",
      selectedFushatGuid: "",
      offerFushatForm: {
        name: "",
        code: "",
        image: "",
      },
      offerFushatSaving: false,
      offerFushatLoading: false,
      offerFushatModalOpen: false,
      offerFushatImageFile: null,
      offerFushatImagePreviewUrl: "",
      offerFushatImageUploading: false,
      offerOfertaListKind: "draft",
      offerOfertaModalOpen: false,
      offerForm: {
        label: "",
        start_at: null,
        end_at: null,
        priority: 0,
        is_price_locked: false,
      },
      priceImportTargetField: "price",
      priceImportFile: null,
      priceImportPreview: null,
      priceImportLoading: false,
      priceImportApplying: false,
      priceImportProgress: { done: 0, total: 0, phase: "" },
      priceImportApplyResult: null,
      priceBatches: [],
      priceBatchLoading: false,
      priceBatchDetails: {},
      priceBatchDetailLoading: null,
      expandedPriceBatchId: null,
      priceBatchRevertId: null,
      priceBatchExportId: null,
      priceBatchCancelId: null,
      priceBatchApplyNowId: null,
      priceBatchResyncId: null,
      offerImportFile: null,
      offerImportPreview: null,
      offerImportLoading: false,
      offerExportLoading: false,
      offerFushatExportLoading: false,
      offerRuleDraft: {
        related_entity: "products",
        scope: "all",
        target_field: "price",
        discount_type: "percentage",
        amount: 0,
        leaf_guid: "",
        product_ids: [],
      },
      offerRules: [],
      offerDrafts: [],
      offerDraftSaving: false,
      offerDraftLoading: false,
      offerPublishLoading: false,
      selectedOfferGuid: "",
      offerDraftGuid: "",
      offerTimeline: [],
      offerTimelineFilter: {
        from: "",
        to: "",
      },
      offerImageFile: null,
      offerImagePreviewUrl: "",
      offerImageUploading: false,
      offerCopyLoading: false,
      offerSimulationRows: [],
      offerSimulationAffected: 0,
      offerSimulationRan: false,
      offerSimulationLoading: false,
      offerRollbackLoading: false,
      offerCancelScheduledLoading: false,
      offerApplyNowLoading: false,
      offerResyncLoading: false,
      offerSyncFailures: [],
      offerSyncFailuresLoading: false,
      offerSyncDetailExpanded: false,
      offerDeleteLoading: false,
      offerFushatDeleteLoading: false,
      offerProcessNowLoading: false,
      offerProcessResult: null,
      offerHealth: {},
      offerAudits: [],
      offerSyncedOk: 0,
      offerSyncedFailed: 0,
      timelineDrag: null,
      timelineBarOverrides: {},
      offerProductSearch: "",
      offerTreeSearch: "",
      offerCollapsedByGuid: {},
      previewRows: [],
      selectedPreviewIds: [],
      applyLoading: false,
      applyResult: null,
      treeSearch: "",
      productSearch: "",
      debouncedProductSearch: "",
      searchDebounceTimer: null,
      customPage: 1,
      customPerPage: 25,
      showCustomTreeFilter: false,
      collapsedByGuid: {},
      offerSection: "fushata",
      offerViewMode: "hub",
      offerWizardStep: 1,
      offerLoadedCampaignStatus: "draft",
      offerFushataListKind: "draft",
      offerNewCampaignModalOpen: false,
      offerCreateOfertaLoading: false,
      priceBatchFilterSearch: "",
      priceBatchFilterStatus: "",
      confirmModal: {
        open: false,
        title: "Konfirmo",
        message: "",
        warning: "",
        details: [],
        confirmWord: "",
        confirmLabel: "Vazhdo",
        danger: false,
        loading: false,
        allowSchedule: false,
        scheduleItemCount: 0,
        action: null,
        payload: null,
      },
      importErrorModal: {
        open: false,
        title: "Gabim gjatë importit",
        message: "",
        source: "",
        errors: [],
        payload: null,
      },
    };
  },
  computed: {
    productsByEntity() {
      return this.allProducts.filter((p) => (this.form.related_entity === "accessories" ? Number(p.is_accessory) === 1 : Number(p.is_accessory) !== 1));
    },
    filteredManualProducts() {
      const q = this.productSearch.trim().toLowerCase();
      if (!q) return this.productsByEntity;
      return this.productsByEntity.filter((p) => `${p.code || ""} ${p.name || ""} ${p.model || ""}`.toLowerCase().includes(q));
    },
    selectedProductsDetails() {
      const picked = new Set(this.form.selected_product_ids.map((id) => Number(id)));
      return this.productsByEntity.filter((p) => picked.has(Number(p.id)));
    },
    visibleTreeNodes() {
      const source = this.categoryTrees[this.form.related_entity] || [];
      const rows = [];
      const q = this.treeSearch.trim().toLowerCase();

      const walk = (nodes, depth) => {
        nodes.forEach((n) => {
          const nameMatch = n.name.toLowerCase().includes(q);
          const childRowsBefore = rows.length;
          const isCollapsed = this.isCollapsed(n.guid);
          const shouldTraverse = !isCollapsed || q;
          if (shouldTraverse && n.children?.length) walk(n.children, depth + 1);
          const hasVisibleDescendant = rows.length > childRowsBefore;
          const show = !q || nameMatch || hasVisibleDescendant;
          if (show) {
            rows.splice(childRowsBefore, 0, {
              guid: n.guid,
              name: n.name,
              depth,
              hasChildren: !!(n.children && n.children.length),
            });
          }
        });
      };

      walk(source, 0);
      return rows;
    },
    canApply() {
      if (this.form.apply_mode === "custom") {
        return this.customChangedCount > 0;
      }
      if (!this.form.amount && this.form.amount !== 0) return false;
      if (this.form.amount < 0) return false;
      if (this.form.scope === "tree") return !!this.form.leaf_guid;
      if (this.form.scope === "selected") return this.form.selected_product_ids.length > 0;
      return true;
    },
    priceScheduleItemCap() {
      return PRICE_SCHEDULE_ITEM_CAP;
    },
    priceImportOverScheduleCap() {
      return this.priceImportValidRows.length > PRICE_SCHEDULE_ITEM_CAP;
    },
    priceImportScheduleSplits() {
      const n = this.priceImportValidRows.length;
      if (n <= 0) return 1;
      return Math.ceil(n / PRICE_SCHEDULE_ITEM_CAP);
    },
    manualOverScheduleCap() {
      return (this.selectedPreviewIds || []).length > PRICE_SCHEDULE_ITEM_CAP;
    },
    manualScheduleSplits() {
      const n = (this.selectedPreviewIds || []).length;
      if (n <= 0) return 1;
      return Math.ceil(n / PRICE_SCHEDULE_ITEM_CAP);
    },
    customRuleRows() {
      const ids = Object.keys(this.customRules).map((id) => Number(id));
      return ids.map((id) => this.productsById[id]).filter(Boolean);
    },
    productsById() {
      const map = {};
      this.productsByEntity.forEach((p) => {
        map[p.id] = p;
      });
      return map;
    },
    customSearchResults() {
      const q = this.debouncedProductSearch.trim().toLowerCase();
      if (!q || q.length < 2) return [];
      const out = [];
      for (let i = 0; i < this.productsByEntity.length; i++) {
        const p = this.productsByEntity[i];
        const row = `${p.code || ""} ${p.name || ""} ${p.model || ""}`.toLowerCase();
        if (row.includes(q)) {
          out.push(p);
          if (out.length >= 40) break;
        }
      }
      return out;
    },
    customFilteredProducts() {
      let rows = this.productsByEntity;
      const q = this.debouncedProductSearch.trim().toLowerCase();
      if (q) {
        rows = rows.filter((p) => `${p.code || ""} ${p.name || ""} ${p.model || ""}`.toLowerCase().includes(q));
      }
      if (this.form.leaf_guid) {
        const selectedGuids = new Set(this.getSelectedBranchGuids(this.form.leaf_guid));
        rows = rows.filter((p) => (this.form.related_entity === "accessories" ? selectedGuids.has(p.accessory_id) : selectedGuids.has(p.type_id)));
      }
      return rows;
    },
    customTotalPages() {
      return Math.max(1, Math.ceil(this.customFilteredProducts.length / this.customPerPage));
    },
    customPageButtons() {
      const total = this.customTotalPages;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const start = Math.max(1, this.customPage - 2);
      const end = Math.min(total, this.customPage + 2);
      const pages = [];
      for (let p = start; p <= end; p++) pages.push(p);
      if (!pages.includes(1)) pages.unshift(1);
      if (!pages.includes(total)) pages.push(total);
      return [...new Set(pages)];
    },
    paginatedCustomProducts() {
      const start = (this.customPage - 1) * this.customPerPage;
      return this.customFilteredProducts.slice(start, start + this.customPerPage);
    },
    customChangedCount() {
      return Object.keys(this.customRules).reduce((acc, id) => (this.isCustomChanged(Number(id)) ? acc + 1 : acc), 0);
    },
    offerProductsByEntity() {
      return this.allProducts.filter((p) => (this.offerRuleDraft.related_entity === "accessories" ? Number(p.is_accessory) === 1 : Number(p.is_accessory) !== 1));
    },
    offerFilteredProducts() {
      const q = this.offerProductSearch.trim().toLowerCase();
      if (!q) return this.offerProductsByEntity.slice(0, 150);
      return this.offerProductsByEntity.filter((p) => `${p.code || ""} ${p.name || ""}`.toLowerCase().includes(q)).slice(0, 150);
    },
    offerVisibleTreeNodes() {
      const source = this.categoryTrees[this.offerRuleDraft.related_entity] || [];
      const rows = [];
      const q = this.offerTreeSearch.trim().toLowerCase();
      const walk = (nodes, depth) => {
        nodes.forEach((n) => {
          const nameMatch = n.name.toLowerCase().includes(q);
          const childRowsBefore = rows.length;
          const isCollapsed = this.isOfferCollapsed(n.guid);
          const shouldTraverse = !isCollapsed || q;
          if (shouldTraverse && n.children?.length) walk(n.children, depth + 1);
          const hasVisibleDescendant = rows.length > childRowsBefore;
          const show = !q || nameMatch || hasVisibleDescendant;
          if (show) {
            rows.splice(childRowsBefore, 0, {
              guid: n.guid,
              name: n.name,
              depth,
              hasChildren: !!(n.children && n.children.length),
            });
          }
        });
      };
      walk(source, 0);
      return rows;
    },
    offerTimelineDays() {
      const from = this.offerTimelineFilter.from;
      const to = this.offerTimelineFilter.to;
      if (!from || !to) return [];
      const start = new Date(`${from}T00:00:00`);
      const end = new Date(`${to}T00:00:00`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

      const out = [];
      const d = new Date(start);
      while (d <= end) {
        out.push({
          key: d.toISOString().slice(0, 10),
          day: String(d.getDate()).padStart(2, "0"),
          month: d.toLocaleDateString("sq-AL", { month: "short" }),
          isWeekend: d.getDay() === 0 || d.getDay() === 6,
          isWeekStart: d.getDay() === 1,
        });
        d.setDate(d.getDate() + 1);
      }
      return out;
    },
    offerTimelineMonths() {
      if (!this.offerTimelineDays.length) return [];
      const months = [];
      let current = null;
      this.offerTimelineDays.forEach((d, idx) => {
        const key = d.key.slice(0, 7);
        const dateObj = new Date(`${d.key}T00:00:00`);
        const label = dateObj.toLocaleDateString("sq-AL", { month: "long", year: "numeric" });
        if (!current || current.key !== key) {
          if (current) months.push(current);
          current = { key, label, start: idx + 1, end: idx + 2 };
        } else {
          current.end = idx + 2;
        }
      });
      if (current) months.push(current);
      return months;
    },
    offerTimelineRows() {
      if (!this.offerTimelineDays.length) return [];
      const first = new Date(`${this.offerTimelineDays[0].key}T00:00:00`);
      const last = new Date(`${this.offerTimelineDays[this.offerTimelineDays.length - 1].key}T00:00:00`);
      const dayCount = this.offerTimelineDays.length;

      const dayDiff = (a, b) => Math.floor((a.getTime() - b.getTime()) / 86400000);

      return this.offerTimeline.map((item) => {
        const startRaw = item.start_at ? new Date(String(item.start_at).slice(0, 10) + "T00:00:00") : new Date(first);
        const endRaw = item.end_at ? new Date(String(item.end_at).slice(0, 10) + "T00:00:00") : new Date(last);
        const clampedStart = startRaw < first ? new Date(first) : startRaw;
        const clampedEnd = endRaw > last ? new Date(last) : endRaw;
        const startIndex = Math.max(0, dayDiff(clampedStart, first));
        const endIndex = Math.max(startIndex + 1, Math.min(dayCount, dayDiff(clampedEnd, first) + 1));
        const entities = new Set((item.rules || []).map((r) => (r.related_entity || "").toLowerCase()));
        const hasProducts = entities.has("product") || entities.has("products");
        const hasAccessories = entities.has("accessory") || entities.has("accessories");
        const entityLabel = hasProducts && hasAccessories ? "Produkte + Aksesore" : hasAccessories ? "Aksesore" : "Produkte";
        const override = this.timelineBarOverrides[item.guid] || null;

        return {
          ...item,
          barStart: override ? override.barStart : startIndex + 1,
          barEnd: override ? override.barEnd : endIndex + 1,
          entityLabel,
          displayLabel: item.label || item.fushat_code || item.code || item.guid,
        };
      });
    },
    offerTimelineGroupedRows() {
      const groups = new Map();
      (this.offerTimelineRows || []).forEach((row) => {
        const key = row.fushat_guid || row.fushat_code || row.code || row.guid;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            fushat_guid: row.fushat_guid,
            fushat_name: row.fushat_name || row.name,
            fushat_code: row.fushat_code || row.code,
            bars: [],
          });
        }
        groups.get(key).bars.push(row);
      });
      return [...groups.values()];
    },
    loadedFushat() {
      return (this.offerFushatList || []).find((f) => f.guid === this.offerFushatGuid) || null;
    },
    ofertaForLoadedFushat() {
      if (!this.offerFushatGuid) return [];
      return (this.offerDrafts || []).filter((item) => item.fushat_guid === this.offerFushatGuid);
    },
    offerOfertaByGroup() {
      const draft = [];
      const activeOrScheduled = [];
      const completed = [];
      this.ofertaForLoadedFushat.forEach((item) => {
        const s = this.effectiveOfferCampaignStatus(item);
        if (s === "completed" || s === "cancelled") completed.push(item);
        else if (s === "active" || s === "scheduled") activeOrScheduled.push(item);
        else draft.push(item);
      });
      return { draft, activeOrScheduled, completed };
    },
    offerOfertaPickerList() {
      if (this.offerOfertaListKind === "draft") return this.offerOfertaByGroup.draft;
      if (this.offerOfertaListKind === "live") return this.offerOfertaByGroup.activeOrScheduled;
      return this.offerOfertaByGroup.completed;
    },
    offerOfertaPickerEmptyLabel() {
      if (this.offerOfertaListKind === "draft") return "Zgjidh ofertë draft...";
      if (this.offerOfertaListKind === "live") return "Zgjidh ofertë aktive ose në pritje...";
      return "Zgjidh ofertë të mbaruar...";
    },
    offerHasIncompleteTreeRule() {
      return this.offerRules.some((r) => r.scope === "tree" && !r.leaf_guid);
    },
    offerCanPublish() {
      return (
        this.offerRules.length > 0 &&
        !this.offerHasIncompleteTreeRule &&
        this.offerCampaignIsDraft &&
        Boolean(this.offerForm.start_at && this.offerForm.end_at) &&
        this.offerSimulationRan
      );
    },
    offerCampaignIsReadOnly() {
      return this.offerLoadedCampaignStatus === "completed" || this.offerLoadedCampaignStatus === "cancelled";
    },
    offerCampaignIsDraft() {
      return !this.offerLoadedCampaignStatus || this.offerLoadedCampaignStatus === "draft";
    },
    offerCampaignIsLiveEditable() {
      return this.offerLoadedCampaignStatus === "scheduled" || this.offerLoadedCampaignStatus === "active";
    },
    offerCampaignIsScheduled() {
      return this.offerLoadedCampaignStatus === "scheduled";
    },
    canCancelScheduledOffer() {
      return Boolean(this.offerDraftGuid) && this.offerCampaignIsScheduled;
    },
    canApplyScheduledOfferNow() {
      return Boolean(this.offerDraftGuid) && this.offerCampaignIsScheduled;
    },
    canResyncOfferFailed() {
      return Boolean(this.offerDraftGuid) && Number(this.offerSyncedFailed || 0) > 0;
    },
    offerSaveButtonLabel() {
      return this.offerCampaignIsDraft ? "Ruaj draft" : "Ruaj ndryshimet";
    },
    canDeleteSelectedOffer() {
      const guid = this.selectedOfferGuid || this.offerDraftGuid;
      if (!guid) return false;
      const item = (this.offerDrafts || []).find((x) => x.guid === guid);
      if (!item) return this.offerCampaignIsDraft;
      return this.effectiveOfferCampaignStatus(item) === "draft";
    },
    offerDraftsByGroup() {
      const draft = [];
      const activeOrScheduled = [];
      const completed = [];
      (this.offerDrafts || []).forEach((item) => {
        const s = this.effectiveOfferCampaignStatus(item);
        if (s === "completed" || s === "cancelled") completed.push(item);
        else if (s === "active" || s === "scheduled") activeOrScheduled.push(item);
        else draft.push(item);
      });
      return { draft, activeOrScheduled, completed };
    },
    offerCampaignPickerList() {
      return this.offerFushatList || [];
    },
    offerCampaignPickerEmptyLabel() {
      return "Zgjidh fushatë...";
    },
    offerFushatModalTitle() {
      if (!this.offerFushatGuid) return "Fushatë e re";
      return "Ndrysho fushatën";
    },
    offerOfertaModalTitle() {
      if (!this.offerDraftGuid) return "Ofertë e re (orar)";
      if (this.offerCampaignIsReadOnly) return "Oferta (orar) — vetëm shikim";
      if (this.offerCampaignIsLiveEditable) return "Ndrysho ofertën (aktive / në pritje)";
      return "Ndrysho ofertën (orar)";
    },
    offerCampaignModalTitle() {
      return this.offerOfertaModalTitle;
    },
    offerLoadedBubbleTitle() {
      if (!this.offerDraftGuid) return "";
      const fushat = this.loadedFushat;
      const fushatLabel = fushat ? `${fushat.code} — ${fushat.name}` : "";
      const ofertaLabel = (this.offerForm.label || "").trim();
      if (fushatLabel && ofertaLabel) return `${fushatLabel} / ${ofertaLabel}`;
      return fushatLabel || ofertaLabel || this.offerDraftGuid;
    },
    offerLoadedBubbleShort() {
      const t = this.offerLoadedBubbleTitle;
      if (!t) return "";
      if (t.length <= 32) return t;
      return `${t.slice(0, 29)}…`;
    },
    offerCampaignIsReadOnlyForSelection() {
      if (!this.selectedOfferGuid) return false;
      const item = (this.offerDrafts || []).find((x) => x.guid === this.selectedOfferGuid);
      if (!item) return false;
      return this.effectiveOfferCampaignStatus(item) === "completed";
    },
    priceImportValidRows() {
      return (this.priceImportPreview?.rows || []).filter((r) => !r.error && r.product_id);
    },
    priceImportErrorRows() {
      return (this.priceImportPreview?.rows || []).filter((r) => r.error);
    },
    priceImportErrorDownloadAvailable() {
      const previewErrors = this.priceImportErrorRows.length;
      const syncFails = (this.priceImportApplyResult?.failed_sync || []).length;
      return previewErrors > 0 || syncFails > 0;
    },
    priceImportProgressLabel() {
      const phase = this.priceImportProgress?.phase;
      if (phase === "live" || phase === "backup") return financaServerLabel(phase);
      return "Financa";
    },
    offerImportErrorRows() {
      return (this.offerImportPreview?.rows || []).filter((r) => r.error);
    },
    offerImportErrorDownloadAvailable() {
      return this.offerImportErrorRows.length > 0;
    },
    priceImportProgressPercent() {
      const total = Number(this.priceImportProgress?.total || 0);
      const done = Number(this.priceImportProgress?.done || 0);
      if (!total) return 0;
      return Math.min(100, Math.round((done / total) * 100));
    },
    offerImportValidRows() {
      return (this.offerImportPreview?.rows || []).filter((r) => !r.error && r.product_id);
    },
    offerSimulationPreviewChunks() {
      if (!this.offerSimulationRan) return [];
      const simRows = Array.isArray(this.offerSimulationRows) ? this.offerSimulationRows : [];
      if (!simRows.length) return [];

      const selectedIdSet = new Set();
      (this.offerRules || []).forEach((rule) => {
        if (rule.scope === "selected" && Array.isArray(rule.product_ids)) {
          rule.product_ids.forEach((id) => selectedIdSet.add(Number(id)));
        }
      });

      const products = this.allProducts || [];
      const findProduct = (pid) => products.find((x) => Number(x.id) === Number(pid));

      const selectedRows = this.sortOfferSimulationRows(simRows.filter((r) => selectedIdSet.has(Number(r.product_id))));

      const chunks = [];
      if (selectedRows.length) {
        chunks.push({
          kind: "flat",
          key: "sim-selected",
          title: "Te zgjedhurat (sipas kodit)",
          subtitle: "Produkte të përfshira nga rregullat me scope “te zgjedhura”.",
          icon: "fa fa-check-square-o",
          iconClass: "sim-preview-icon-selected",
          rows: selectedRows,
        });
      }

      const claimedByTree = new Set();
      const treeRuleEntries = (this.offerRules || [])
        .map((rule, idx) => ({ rule, idx }))
        .filter(({ rule }) => rule.scope === "tree" && rule.leaf_guid);

      treeRuleEntries.forEach(({ rule, idx }) => {
        const guidSet = new Set(this.getOfferTreeBranchGuids(rule.related_entity, rule.leaf_guid).map(String));
        const rows = [];
        simRows.forEach((r) => {
          const id = Number(r.product_id);
          if (selectedIdSet.has(id) || claimedByTree.has(id)) return;
          const p = findProduct(id);
          if (!p) return;
          const catGuid = this.offerProductCategoryGuid(p, rule.related_entity);
          if (!catGuid || !guidSet.has(String(catGuid))) return;
          rows.push(r);
          claimedByTree.add(id);
        });
        if (!rows.length) return;

        const pathLabels = this.getOfferCategoryPathLabels(rule.related_entity, rule.leaf_guid);
        const pathJoined = pathLabels.length
          ? pathLabels.join(" › ")
          : this.getOfferCategoryName(rule.related_entity, rule.leaf_guid);

        const catMap = new Map();
        this.sortOfferSimulationRows(rows).forEach((r) => {
          const p = findProduct(Number(r.product_id));
          const cGuid = p ? this.offerProductCategoryGuid(p, rule.related_entity) : `unk-${r.product_id}`;
          const pathLabs = p ? this.getOfferProductCategoryPathLabels(rule.related_entity, p) : [];
          if (!catMap.has(cGuid)) {
            catMap.set(cGuid, {
              categoryKey: cGuid,
              categoryTitle: pathLabs.length ? pathLabs.join(" › ") : `Kategori: ${cGuid}`,
              depth: Math.max(0, pathLabs.length - 1),
              rows: [],
            });
          }
          catMap.get(cGuid).rows.push(r);
        });

        const groups = [...catMap.values()]
          .map((g) => ({ ...g, rows: this.sortOfferSimulationRows(g.rows) }))
          .sort((a, b) => a.categoryTitle.localeCompare(b.categoryTitle, "sq"));

        chunks.push({
          kind: "tree",
          key: `sim-tree-${idx}`,
          title: `Pema #${idx + 1}`,
          subtitle: pathJoined,
          entityLabel: this.offerEntityLabel(rule.related_entity),
          groups,
        });
      });

      const otherRows = this.sortOfferSimulationRows(
        simRows.filter((r) => {
          const id = Number(r.product_id);
          return !selectedIdSet.has(id) && !claimedByTree.has(id);
        }),
      );
      if (otherRows.length) {
        chunks.push({
          kind: "flat",
          key: "sim-other",
          title: "Nga rregullat e tjera",
          subtitle: "P.sh. scope “të gjitha” ose produkte që mbeten jashtë pemëve të raportuara mësipër.",
          icon: "fa fa-th",
          iconClass: "sim-preview-icon-other",
          rows: otherRows,
        });
      }

      return chunks;
    },
  },
  methods: {
    switchToOfertaTab() {
      this.activeTab = "oferta";
      if (!this.offerDraftGuid) {
        this.offerViewMode = "hub";
      }
      this.setOfferTimelineFilterToCurrentMonth();
      this.loadOfferTimeline();
    },
    goToOfferHub() {
      this.offerViewMode = "hub";
      this.offerWizardStep = 1;
    },
    setOfferViewModeFromStatus(status) {
      if (status === "scheduled" || status === "active" || status === "completed") {
        this.offerViewMode = "live";
      } else if (this.offerDraftGuid) {
        this.offerViewMode = "wizard";
        this.offerWizardStep = 1;
      } else {
        this.offerViewMode = "hub";
      }
    },
    openConfirmModal(options) {
      this.confirmModal = {
        open: true,
        title: options.title || "Konfirmo",
        message: options.message || "",
        warning: options.warning || "",
        details: options.details || [],
        confirmWord: options.confirmWord || "",
        confirmLabel: options.confirmLabel || "Vazhdo",
        danger: Boolean(options.danger),
        loading: false,
        allowSchedule: Boolean(options.allowSchedule),
        scheduleItemCount: Number(options.scheduleItemCount || 0),
        action: options.action || null,
        payload: options.payload ?? null,
      };
    },
    closeConfirmModal(force = false) {
      if (this.confirmModal.loading && !force) return;
      this.confirmModal.open = false;
      this.confirmModal.loading = false;
      this.confirmModal.allowSchedule = false;
      this.confirmModal.scheduleItemCount = 0;
      this.confirmModal.action = null;
      this.confirmModal.payload = null;
    },
    openImportErrorModal(options) {
      const errors = Array.isArray(options.errors) ? options.errors : [];
      const payload = options.payload || {
        source: options.source || "",
        message: options.message || "",
        errors,
      };
      this.importErrorModal = {
        open: true,
        title: options.title || "Gabim gjatë importit",
        message: options.message || "",
        source: options.source || "",
        errors,
        payload,
      };
    },
    closeImportErrorModal() {
      this.importErrorModal.open = false;
    },
    extractApiErrorPayload(e) {
      const data = e?.response?.data;
      if (data && typeof data === "object" && !(data instanceof Blob)) {
        return data;
      }
      return { message: e?.message || "Gabim i panjohur" };
    },
    isTransientDbError(e) {
      const data = this.extractApiErrorPayload(e);
      const msg = `${data.message || ""} ${data.response?.message || ""} ${e?.message || ""}`;
      return /SQLSTATE\[HY000\] \[2002\]|SQLSTATE\[HY000\] \[2006\]|Connection refused|server has gone away|Lost connection/i.test(msg);
    },
    isNetworkTimeoutError(e) {
      const data = this.extractApiErrorPayload(e);
      const msg = `${data.message || ""} ${data.response?.message || ""} ${e?.message || ""} ${e?.code || ""}`;
      return /Network Error|timeout|ECONNABORTED|ERR_NETWORK|ERR_CONNECTION/i.test(msg);
    },
    sleepMs(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async postPricingApplyWithRetry(payload) {
      const attempts = 2;
      let lastError = null;
      for (let i = 0; i < attempts; i++) {
        try {
          return await Api(true).post("product/pricing-offers/apply", payload);
        } catch (e) {
          lastError = e;
          if (!this.isTransientDbError(e) || i === attempts - 1) {
            throw e;
          }
          await this.sleepMs(800 * (i + 1));
        }
      }
      throw lastError;
    },
    apiErrorMessage(e, fallback) {
      const data = this.extractApiErrorPayload(e);
      return data.message || data.error || e?.message || fallback;
    },
    previewRowsToImportErrors(rows) {
      return (rows || [])
        .filter((r) => r && r.error)
        .map((r) => ({
          line: r.line,
          code: r.code,
          name: r.name,
          reason: r.error,
        }));
    },
    failedSyncToImportErrors(failedSync) {
      return (failedSync || []).map((f) => ({
        product_id: f.product_id,
        code: f.code,
        name: f.name,
        erp: f.erp,
        status: f.status,
        method: f.method,
        reason: f.reason || "F5 sync failed",
      }));
    },
    flattenValidationErrors(errorsObj) {
      if (!errorsObj || typeof errorsObj !== "object") return [];
      const out = [];
      Object.keys(errorsObj).forEach((field) => {
        const messages = Array.isArray(errorsObj[field]) ? errorsObj[field] : [errorsObj[field]];
        messages.forEach((reason) => {
          out.push({ code: field, reason: String(reason) });
        });
      });
      return out;
    },
    downloadJsonPayload(payload, filename) {
      const blob = new Blob([JSON.stringify(payload || {}, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    downloadImportErrorJson() {
      const payload = this.importErrorModal.payload || {
        source: this.importErrorModal.source,
        message: this.importErrorModal.message,
        errors: this.importErrorModal.errors,
      };
      const source = payload.source || this.importErrorModal.source || "import";
      this.downloadJsonPayload(payload, `${source}_gabime.json`);
    },
    buildPriceImportErrorPayload() {
      const previewErrors = this.previewRowsToImportErrors(this.priceImportPreview?.rows);
      const syncErrors = this.failedSyncToImportErrors(this.priceImportApplyResult?.failed_sync);
      const errors = [...previewErrors, ...syncErrors];
      const source = syncErrors.length ? "cmimet_apply" : "cmimet_preview";
      return {
        source,
        message: syncErrors.length
          ? (financaBulkFailureText(this.priceImportApplyResult?.failed_sync) || `${syncErrors.length} nuk u ruajtën në Financa 1 ose Financa 2.`)
          : `${previewErrors.length} rreshta me gabime në skedar.`,
        errors,
      };
    },
    downloadPriceImportErrorsJson() {
      this.downloadJsonPayload(this.buildPriceImportErrorPayload(), "cmimet_import_gabime.json");
    },
    downloadOfferImportErrorsJson() {
      const errors = this.previewRowsToImportErrors(this.offerImportPreview?.rows);
      this.downloadJsonPayload(
        {
          source: "oferta_preview",
          message: `${errors.length} rreshta me gabime në skedar.`,
          errors,
        },
        "oferta_import_gabime.json",
      );
    },
    erpLabel(erp) {
      return financaServerLabel(erp);
    },
    async onConfirmModalConfirm(timingPayload) {
      const action = this.confirmModal.action;
      if (!action) {
        this.closeConfirmModal(true);
        return;
      }
      this.confirmModal.loading = true;
      try {
        const payload = {
          ...(this.confirmModal.payload && typeof this.confirmModal.payload === "object" ? this.confirmModal.payload : {}),
          ...(timingPayload && typeof timingPayload === "object" ? timingPayload : {}),
        };
        await action(payload);
        this.closeConfirmModal(true);
      } catch (e) {
        // action handles toast errors
      } finally {
        this.confirmModal.loading = false;
      }
    },
    priceBatchStatusLabel(status) {
      if (status === "applying") return "Duke aplikuar";
      if (status === "failed") return "Deshtuar";
      if (status === "reverted") return "Rikthyer";
      if (status === "applied") return "Aplikuar";
      if (status === "scheduled") return "Ne pritje";
      if (status === "cancelled") return "Anuluar";
      return status || "—";
    },
    priceBatchStatusClass(status) {
      if (status === "applying") return "tag is-info is-light";
      if (status === "failed") return "tag is-danger is-light";
      if (status === "reverted") return "tag is-warning";
      if (status === "scheduled") return "tag is-info";
      if (status === "cancelled") return "tag is-light";
      return "tag is-success";
    },
    async finalizePriceBatchStatus(batchId, status) {
      if (!batchId) return;
      try {
        await Api(true).post("product/pricing-offers/price-batches/finalize", {
          batch_id: batchId,
          status,
        });
      } catch (_) {
        /* ignore finalize error */
      }
    },
    priceBatchFinalStatus({ updated, synced_ok }) {
      if (Number(synced_ok) > 0 || Number(updated) > 0) return "applied";
      return "failed";
    },
    formatLocalDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    scheduleCapWarning(count) {
      const cap = PRICE_SCHEDULE_ITEM_CAP;
      const batches = Math.max(1, Math.ceil(count / cap));
      if (count <= cap) return "";
      return `Ke ${count} produkte. Do të ndahet automatikisht në ${batches} batch-e (max ${cap} secili).`;
    },
    chunkScheduleItems(items) {
      const cap = PRICE_SCHEDULE_ITEM_CAP;
      const chunks = [];
      for (let i = 0; i < items.length; i += cap) {
        chunks.push(items.slice(i, i + cap));
      }
      return chunks;
    },
    async postScheduledChunks({ source, filename, target_field, apply_at, items }) {
      const chunks = this.chunkScheduleItems(items);
      const created = [];
      for (let i = 0; i < chunks.length; i++) {
        const partName = chunks.length > 1
          ? `${filename || source} (${i + 1}/${chunks.length})`
          : filename;
        const res = await Api(true).post("product/pricing-offers/price-batches/schedule", {
          source,
          filename: partName,
          target_field,
          apply_at,
          items: chunks[i],
        });
        created.push(res?.data?.batch);
      }
      return created;
    },
    canRevertPriceBatch(batch) {
      if (!batch?.id) return false;
      const status = batch.status || "applied";
      if (status !== "applied") return false;
      return (batch.target_field || "price") === "price";
    },
    canResyncPriceBatchFailed(batch) {
      if (!batch?.id) return false;
      const status = batch.status || "";
      if (status !== "applied" && status !== "failed") return false;
      return Number(batch.synced_failed || 0) > 0;
    },
    requestApplyPriceImport() {
      const rows = this.priceImportValidRows;
      if (!rows.length) {
        this.$toast.error("Nuk ka rreshta të vlefshme për import.");
        return;
      }
      this.openConfirmModal({
        title: "Apliko importin e cmimeve",
        message: `Do të aplikohen ${rows.length} rreshta. Çmimet shkojnë së pari plotësisht në Financa 2, pastaj plotësisht në Financa 1.`,
        details: [
          `Lloji: ${this.priceImportTargetField === "default_price" ? "Cmimi bazë" : "Cmimi"}`,
          `Skedari: ${this.priceImportFile?.name || "—"}`,
        ],
        warning: "Ky veprim ndryshon çmimet në sistem dhe sinkronizon Financa 2 pastaj Financa 1. Kontrollo preview-n para se të vazhdosh.",
        confirmLabel: "Apliko",
        allowSchedule: true,
        scheduleItemCount: rows.length,
        action: (timing) => this.applyPriceImportContinue(timing),
      });
    },
    requestApplyDiscount() {
      if (!this.previewRows.length) {
        this.refreshPreview();
      }
      if (!this.previewRows.length || this.selectedPreviewIds.length === 0) {
        this.$toast.error("Nuk ka produkte per perditesim.");
        return;
      }
      this.openConfirmModal({
        title: "Apliko uljen e cmimeve",
        message: `Do të përditësohen ${this.selectedPreviewIds.length} produkte dhe do të sinkronizohen në Financa 1 dhe Financa 2.`,
        details: [
          `Fusha: ${this.form.target_field === "default_price" ? "Cmimi bazë" : "Cmimi"}`,
          `Mënyra: ${this.form.apply_mode === "custom" ? "Ulje të ndryshme" : "Ulje e njëjtë"}`,
        ],
        warning: "Ndryshimet regjistrohen në audit dhe mund të rikthehen (vetëm për cmimin e shitjes).",
        confirmLabel: "Apliko",
        allowSchedule: true,
        scheduleItemCount: this.selectedPreviewIds.length,
        action: (timing) => this.applyDiscount(timing),
      });
    },
    requestRevertPriceBatch(batch) {
      if (!this.canRevertPriceBatch(batch)) return;
      this.openConfirmModal({
        title: "Rikthe cmimet e batch-it",
        message: `Rikthe cmimet e batch #${batch.id}?`,
        warning: "Cmimi i produktit do të bëhet i barabartë me cmimin bazë aktual dhe do të sinkronizohet në Financa 1 dhe Financa 2.",
        confirmLabel: "Rikthe",
        danger: true,
        action: () => this.revertPriceBatch(batch, true),
      });
    },
    requestResyncPriceBatchFailed(batch) {
      if (!this.canResyncPriceBatchFailed(batch)) return;
      const failed = Number(batch.synced_failed || 0);
      this.openConfirmModal({
        title: "Riprovo Financa",
        message: `Riprovo sinkronizimin në Financa 1 dhe Financa 2 për ${failed} produkte të dështuara në batch #${batch.id}?`,
        warning: "Çmimet në Globe nuk ndryshohen. Dërgohet përsëri artikulli aktual në Financa 1 dhe Financa 2.",
        confirmLabel: "Riprovo Financa",
        action: () => this.resyncPriceBatchFailed(batch),
      });
    },
    requestDeleteSelectedOfferDraft() {
      const guid = this.selectedOfferGuid || this.offerDraftGuid;
      if (!guid) return;
      this.openConfirmModal({
        title: "Fshi draft oferte",
        message: "Fshi këtë draft oferte? Veprimi nuk kthehet mbrapsht.",
        confirmLabel: "Fshi",
        danger: true,
        action: () => this.deleteSelectedOfferDraft(true),
      });
    },
    requestDeleteLoadedFushat() {
      if (!this.offerFushatGuid) return;
      this.openConfirmModal({
        title: "Fshi fushatën",
        message: "Fshi këtë fushatë dhe draftet e saj?",
        warning: "Ofertat aktive ose në pritje bllokojnë fshirjen.",
        confirmLabel: "Fshi",
        danger: true,
        action: () => this.deleteLoadedFushat(true),
      });
    },
    requestPublishOfferDraft() {
      if (!this.offerRules.length) {
        this.$toast.error("Shto te pakten nje rregull para publikimit.");
        return;
      }
      if (this.offerHasIncompleteTreeRule) {
        this.$toast.error("Ke rregull peme pa kategori.");
        return;
      }
      if (!this.offerSimulationRan) {
        this.$toast.error("Ekzekuto parashikimin para publikimit.");
        return;
      }
      this.openConfirmModal({
        title: "Publiko ofertën",
        message: "Oferta do të publikohet sipas orarit të vendosur.",
        confirmWord: "PUBLIKO",
        confirmLabel: "Publiko",
        action: () => this.publishOfferDraft(true),
      });
    },
    requestRollbackOfferDraft() {
      if (!this.offerDraftGuid) return;
      this.openConfirmModal({
        title: "Ndalo ofertën",
        message: "Oferta do të ndalojë dhe çmimet do të rikthehen te bazë / oferta tjetër aktive. Kjo nuk është e njëjtë me Anulo (vetëm për oferta në pritje, pa prekur çmimet).",
        confirmWord: "NDALO",
        confirmLabel: "Ndalo",
        danger: true,
        action: () => this.rollbackOfferDraft(true),
      });
    },
    requestCancelScheduledOffer() {
      if (!this.canCancelScheduledOffer) return;
      this.openConfirmModal({
        title: "Anulo ofertën në pritje",
        message: "Oferta në pritje anulohet. Çmimet NUK ndryshojnë (nuk ka qenë aktive).",
        confirmWord: "ANULO",
        confirmLabel: "Anulo",
        danger: true,
        action: () => this.cancelScheduledOffer(true),
      });
    },
    requestApplyScheduledOfferNow() {
      if (!this.canApplyScheduledOfferNow) return;
      this.openConfirmModal({
        title: "Apliko ofertën tani",
        message: "Oferta në pritje aktivizohet menjëherë (pa pritur datën e fillimit) dhe çmimet sinkronizohen në Financa 1 dhe Financa 2.",
        confirmWord: "APLIKO",
        confirmLabel: "Apliko tani",
        action: () => this.applyScheduledOfferNow(true),
      });
    },
    requestResyncOfferFailed() {
      if (!this.canResyncOfferFailed) return;
      this.openConfirmModal({
        title: "Riprovo Financa",
        message: `Riprovo sinkronizimin në Financa 1 dhe Financa 2 për ${this.offerSyncedFailed} produkte të dështuara. Çmimi në Globe nuk ndryshon.`,
        confirmWord: "RIPROVO",
        confirmLabel: "Riprovo Financa",
        action: () => this.resyncOfferFailed(true),
      });
    },
    maybeOpenOfferSyncErrorModal(result, source) {
      const failedSync = result?.failed_sync || [];
      const syncedFailed = Number(result?.synced_failed || 0);
      if (!failedSync.length && syncedFailed <= 0) return;
      this.openImportErrorModal({
        source: source || "oferta_apply",
        title: "Dështime sinkronizimi Financa 1 / Financa 2",
        message: financaBulkFailureText(failedSync) || `${syncedFailed || failedSync.length} nuk u ruajtën në Financa 1 ose Financa 2.`,
        errors: this.failedSyncToImportErrors(failedSync),
      });
    },
    applyOfferSyncCountsFromItemOrResult(item, result) {
      if (item) {
        this.offerSyncedOk = Number(item.synced_ok ?? this.offerSyncedOk ?? 0);
        this.offerSyncedFailed = Number(item.synced_failed ?? this.offerSyncedFailed ?? 0);
      }
      if (result) {
        if (result.synced_ok != null) this.offerSyncedOk = Number(result.synced_ok);
        if (result.synced_failed != null) this.offerSyncedFailed = Number(result.synced_failed);
      }
    },
    async loadOfferSyncFailures(options = {}) {
      if (!this.offerDraftGuid) {
        this.offerSyncFailures = [];
        return;
      }
      this.offerSyncFailuresLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/sync-failures", {
          guid: this.offerDraftGuid,
          only_failed: true,
          limit: 500,
        });
        this.offerSyncFailures = Array.isArray(res?.data?.items) ? res.data.items : [];
        this.offerSyncedOk = Number(res?.data?.synced_ok ?? this.offerSyncedOk);
        this.offerSyncedFailed = Number(res?.data?.synced_failed ?? this.offerSyncedFailed);
      } catch (e) {
        this.offerSyncFailures = [];
        if (!options.silent) {
          this.$toast.error(e?.response?.data?.message || "Nuk u ngarkuan deshtimet F5.");
        }
      } finally {
        this.offerSyncFailuresLoading = false;
      }
    },
    async downloadOfferSyncCsv(onlyFailed = false) {
      if (!this.offerDraftGuid) return;
      try {
        const res = await Api(true).post(
          "product/pricing-offers/campaign/export-sync-csv",
          { guid: this.offerDraftGuid, only_failed: onlyFailed },
          { responseType: "blob" },
        );
        this.saveBlobDownload(res, onlyFailed ? "oferta-f5-deshtime.csv" : "oferta-f5-sync.csv");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u shkarkua CSV.");
      }
    },
    async cancelScheduledOffer(skipConfirm) {
      if (!this.offerDraftGuid) return;
      if (!skipConfirm) {
        this.requestCancelScheduledOffer();
        return;
      }
      this.offerCancelScheduledLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/cancel-scheduled", {
          guid: this.offerDraftGuid,
        });
        if (res?.data?.item) this.hydrateOfferDraft(res.data.item);
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        await this.loadOfferAudits();
        this.setOfferViewModeFromStatus(this.offerLoadedCampaignStatus);
        this.$toast.success(res?.data?.message || "Oferta u anulua.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate anulimit.");
      } finally {
        this.offerCancelScheduledLoading = false;
      }
    },
    async applyScheduledOfferNow(skipConfirm) {
      if (!this.offerDraftGuid) return;
      if (!skipConfirm) {
        this.requestApplyScheduledOfferNow();
        return;
      }
      this.offerApplyNowLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/apply-now", {
          guid: this.offerDraftGuid,
        });
        if (res?.data?.item) this.hydrateOfferDraft(res.data.item);
        this.applyOfferSyncCountsFromItemOrResult(res?.data?.item, res?.data?.result);
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        await this.loadOfferAudits();
        await this.loadOfferSyncFailures({ silent: true });
        this.setOfferViewModeFromStatus(this.offerLoadedCampaignStatus);
        this.$toast.success(res?.data?.message || "Oferta u aktivizua tani.");
        this.maybeOpenOfferSyncErrorModal(res?.data?.result, "oferta_apply");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate aplikimit.");
      } finally {
        this.offerApplyNowLoading = false;
      }
    },
    async resyncOfferFailed(skipConfirm) {
      if (!this.offerDraftGuid) return;
      if (!skipConfirm) {
        this.requestResyncOfferFailed();
        return;
      }
      this.offerResyncLoading = true;
      try {
        await this.loadOfferSyncFailures({ silent: true });
        const failedIds = (this.offerSyncFailures || [])
          .map((r) => Number(r.product_id))
          .filter(Boolean);
        const chunks = [];
        for (let i = 0; i < failedIds.length; i += OFFER_RESYNC_CHUNK_SIZE) {
          chunks.push(failedIds.slice(i, i + OFFER_RESYNC_CHUNK_SIZE));
        }
        if (chunks.length === 0) {
          chunks.push([]);
        }
        const failed_sync = [];
        let syncedOk = this.offerSyncedOk;
        let syncedFailed = this.offerSyncedFailed;
        for (const chunk of chunks) {
          const res = await Api(true).post("product/pricing-offers/campaign/resync-failed", {
            guid: this.offerDraftGuid,
            product_ids: chunk.length ? chunk : undefined,
          });
          if (res?.data?.item) this.hydrateOfferDraft(res.data.item);
          syncedOk = Number(res?.data?.synced_ok ?? syncedOk);
          syncedFailed = Number(res?.data?.synced_failed ?? syncedFailed);
          if (Array.isArray(res?.data?.failed_sync)) {
            failed_sync.push(...res.data.failed_sync);
          }
        }
        this.offerSyncedOk = syncedOk;
        this.offerSyncedFailed = syncedFailed;
        await this.loadOfferAudits();
        await this.loadOfferSyncFailures({ silent: true });
        if (syncedFailed > 0) {
          this.$toast.error(financaBulkFailureText(failed_sync) || `Mbeten ${syncedFailed} që nuk u ruajtën në Financa 1 ose Financa 2.`);
          this.maybeOpenOfferSyncErrorModal({ failed_sync, synced_failed: syncedFailed }, "oferta_resync");
        } else {
          this.$toast.success("U ruajtën në Financa 1 dhe Financa 2.");
        }
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate riprovimit F5.");
      } finally {
        this.offerResyncLoading = false;
      }
    },
    offerWizardCanContinueStep1() {
      return Boolean(this.offerForm.start_at && this.offerForm.end_at);
    },
    offerWizardCanContinueStep2() {
      return this.offerRules.length > 0 && !this.offerHasIncompleteTreeRule;
    },
    offerWizardCanContinueStep3() {
      return this.offerSimulationRan;
    },
    async offerWizardContinue() {
      if (this.offerWizardStep === 1) {
        if (!this.offerWizardCanContinueStep1()) {
          this.$toast.error("Vendos datën e fillimit dhe mbarimit.");
          return;
        }
        const ok = await this.saveOfferDraft({ silent: true });
        if (!ok) return;
        this.offerWizardStep = 2;
        return;
      }
      if (this.offerWizardStep === 2) {
        if (!this.offerWizardCanContinueStep2()) {
          this.$toast.error("Shto të paktën një rregull të vlefshëm.");
          return;
        }
        const ok = await this.saveOfferDraft({ silent: true });
        if (!ok) return;
        this.offerWizardStep = 3;
        return;
      }
      if (this.offerWizardStep === 3) {
        if (!this.offerWizardCanContinueStep3()) {
          await this.simulateOfferDraft({ announce: true });
          if (!this.offerSimulationRan) return;
        }
        this.offerWizardStep = 4;
      }
    },
    offerWizardBack() {
      if (this.offerWizardStep > 1) {
        this.offerWizardStep -= 1;
      }
    },
    isCollapsed(guid) {
      return !!this.collapsedByGuid[guid];
    },
    toggleNode(guid) {
      this.collapsedByGuid = { ...this.collapsedByGuid, [guid]: !this.collapsedByGuid[guid] };
    },
    selectTreeNode(node) {
      this.form.leaf_guid = node.guid;
    },
    isSelected(id) {
      return this.form.selected_product_ids.includes(String(id));
    },
    toggleProductSelection(id) {
      const key = String(id);
      if (this.form.selected_product_ids.includes(key)) {
        this.form.selected_product_ids = this.form.selected_product_ids.filter((x) => x !== key);
      } else {
        this.form.selected_product_ids = [...this.form.selected_product_ids, key];
      }
    },
    selectFilteredProducts() {
      const merge = new Set(this.form.selected_product_ids);
      this.filteredManualProducts.forEach((p) => merge.add(String(p.id)));
      this.form.selected_product_ids = [...merge];
    },
    clearSelectedProducts() {
      this.form.selected_product_ids = [];
    },
    removeSelectedProduct(id) {
      this.form.selected_product_ids = this.form.selected_product_ids.filter((x) => x !== String(id));
    },
    getCustomRule(id) {
      return this.customRules[id] || { discount_type: "flat", amount: 0 };
    },
    setCustomRuleType(id, value) {
      this.customRules = {
        ...this.customRules,
        [id]: { ...this.getCustomRule(id), discount_type: value },
      };
    },
    setCustomRuleAmount(id, value) {
      const numeric = Number(value || 0);
      this.customRules = {
        ...this.customRules,
        [id]: { ...this.getCustomRule(id), amount: numeric < 0 ? 0 : numeric },
      };
    },
    isCustomChanged(id) {
      const product = this.productsById[id];
      if (!product) return false;
      const rule = this.getCustomRule(id);
      if (!rule || (!rule.amount && rule.amount !== 0)) return false;
      const base = Number(product[this.form.target_field] || 0);
      const next = this.calcNewPrice(base, rule.discount_type, rule.amount);
      return Number(next) !== Number(base);
    },
    clearCustomRules() {
      this.customRules = {};
      this.clearPriceImportState();
    },
    clearPriceImportState() {
      this.priceImportPreview = null;
      this.priceImportFile = null;
      this.priceImportApplyResult = null;
      this.priceImportProgress = { done: 0, total: 0, phase: "" };
    },
    onPriceImportTargetFieldChange(value) {
      this.priceImportTargetField = value === "default_price" ? "default_price" : "price";
      this.clearPriceImportState();
    },
    clearTreeFilter() {
      this.treeSearch = "";
      this.form.leaf_guid = "";
    },
    clearCustomFilters() {
      this.productSearch = "";
      this.debouncedProductSearch = "";
      this.treeSearch = "";
      this.form.leaf_guid = "";
      this.customPage = 1;
      this.showCustomTreeFilter = false;
    },
    money(value) {
      const num = Number(value || 0);
      return new Intl.NumberFormat("sq-AL", { maximumFractionDigits: 2 }).format(num);
    },
    getTargetProducts() {
      if (this.form.apply_mode === "custom") {
        return Object.keys(this.customRules)
          .map((id) => Number(id))
          .filter((id) => this.isCustomChanged(id))
          .map((id) => this.productsById[id])
          .filter(Boolean);
      }
      if (this.form.scope === "tree") {
        const selectedGuids = new Set(this.getSelectedBranchGuids(this.form.leaf_guid));
        return this.productsByEntity.filter((p) => {
          if (this.form.related_entity === "accessories") return selectedGuids.has(p.accessory_id);
          return selectedGuids.has(p.type_id);
        });
      }
      if (this.form.scope === "selected") {
        const picked = new Set(this.form.selected_product_ids.map((id) => Number(id)));
        return this.productsByEntity.filter((p) => picked.has(Number(p.id)));
      }
      return this.productsByEntity;
    },
    getSelectedBranchGuids(rootGuid) {
      if (!rootGuid) return [];
      const roots = this.categoryTrees[this.form.related_entity] || [];
      const stack = [...roots];
      let rootNode = null;
      while (stack.length) {
        const current = stack.pop();
        if (current.guid === rootGuid) {
          rootNode = current;
          break;
        }
        if (current.children?.length) {
          current.children.forEach((ch) => stack.push(ch));
        }
      }
      if (!rootNode) return [rootGuid];

      const all = [];
      const walk = (node) => {
        all.push(node.guid);
        if (node.children?.length) {
          node.children.forEach((child) => walk(child));
        }
      };
      walk(rootNode);
      return all;
    },
    calcNewPrice(baseValue, discountType, amountRaw) {
      const base = Number(baseValue || 0);
      const amount = Number(amountRaw || 0);
      if (discountType === "fixed_price") {
        return Number(Math.max(0, amount).toFixed(2));
      }
      let next = discountType === "percentage" ? base - base * (amount / 100) : base - amount;
      if (next < 0) next = 0;
      return Number(next.toFixed(2));
    },
    refreshPreview() {
      const targets = this.getTargetProducts();
      this.previewRows = targets.map((p) => {
        const rule = this.form.apply_mode === "custom" ? this.customRules[p.id] : null;
        const discountType = rule?.discount_type || this.form.discount_type;
        const amount = rule?.amount ?? this.form.amount;
        const base = Number(p[this.form.target_field] || 0);
        return {
          id: p.id,
          code: p.code,
          name: p.name,
          base_value: base,
          new_price: this.calcNewPrice(base, discountType, amount),
        };
      });
      this.selectedPreviewIds = this.previewRows.map((r) => r.id);
    },
    buildPayloadForIds(idsChunk) {
      if (this.form.apply_mode === "custom") {
        return {
          mode: "custom",
          related_entity: this.form.related_entity,
          target_field: this.form.target_field,
          scope: "selected",
          product_ids: idsChunk,
          product_rules: idsChunk.map((id) => ({
            product_id: id,
            discount_type: this.customRules[id]?.discount_type || "flat",
            amount: Number(this.customRules[id]?.amount || 0),
          })),
        };
      }
      return {
        mode: "bulk",
        related_entity: this.form.related_entity,
        target_field: this.form.target_field,
        scope: "selected",
        discount_type: this.form.discount_type,
        amount: Number(this.form.amount || 0),
        leaf_guid: null,
        product_ids: idsChunk,
      };
    },
    async applyDiscount(timing) {
      if (!this.previewRows.length) {
        this.refreshPreview();
      }
      if (!this.previewRows.length || this.selectedPreviewIds.length === 0) {
        this.$toast.error("Nuk ka produkte per perditesim.");
        return;
      }
      if (timing?.applyAt) {
        await this.scheduleManualDiscount(timing.applyAt);
        return;
      }
      this.applyLoading = true;
      this.applyResult = null;
      let batchId = null;
      try {
        const targetIds = this.selectedPreviewIds.map((id) => Number(id));
        const createRes = await Api(true).post("product/pricing-offers/price-batches", {
          source: "manual",
          filename: null,
          target_field: this.form.target_field === "default_price" ? "default_price" : "price",
          product_count: targetIds.length,
          status: "applying",
        });
        batchId = createRes?.data?.batch?.id;
        if (!batchId) {
          throw new Error("Batch audit nuk u krijua.");
        }

        const chunkSize = 5;
        let updated = 0;
        let targeted = 0;
        let synced_ok = 0;
        let synced_failed = 0;
        const failed_sync = [];

        for (let i = 0; i < targetIds.length; i += chunkSize) {
          const idsChunk = targetIds.slice(i, i + chunkSize);
          const payload = this.buildPayloadForIds(idsChunk);
          payload.batch_id = batchId;
          const res = await this.postPricingApplyWithRetry(payload);
          targeted += Number(res.data.targeted || idsChunk.length);
          updated += Number(res.data.updated || 0);
          synced_ok += Number(res.data.synced_ok || 0);
          synced_failed += Number(res.data.synced_failed || 0);
          if (Array.isArray(res.data.failed_sync)) {
            failed_sync.push(...res.data.failed_sync);
          }
        }

        const finalStatus = this.priceBatchFinalStatus({ updated, synced_ok });
        await Api(true).post("product/pricing-offers/price-batches/finalize", {
          batch_id: batchId,
          status: finalStatus,
        });

        this.applyResult = { targeted, updated, synced_ok, synced_failed, failed_sync };
        const applyNote = synced_failed > 0
          ? (financaBulkFailureText(failed_sync) || `${synced_failed} nuk u ruajtën në Financa 1 ose Financa 2.`)
          : (synced_ok > 0 ? `${synced_ok} u ruajtën në Financa 1 dhe Financa 2.` : "");
        const applyText = `Targetuar: ${targeted}, përditësuar: ${updated}.${applyNote ? ` ${applyNote}` : ""}`;
        if (synced_failed > 0) this.$toast.warning(applyText);
        else this.$toast.success(applyText);
        this.allProducts = await ProductServices.getProducts();
        this.clearCustomRules();
        this.refreshPreview();
        await this.loadPriceBatches();
      } catch (e) {
        await this.finalizePriceBatchStatus(batchId, "failed");
        this.$toast.error(e?.response?.data?.message || e?.message || "Gabim gjate aplikimit te cmimeve.");
        await this.loadPriceBatches();
      } finally {
        this.applyLoading = false;
      }
    },
    togglePreviewSelection(id) {
      if (this.selectedPreviewIds.includes(id)) {
        this.selectedPreviewIds = this.selectedPreviewIds.filter((x) => x !== id);
      } else {
        this.selectedPreviewIds = [...this.selectedPreviewIds, id];
      }
    },
    removeFromPreview(id) {
      this.previewRows = this.previewRows.filter((r) => r.id !== id);
      this.selectedPreviewIds = this.selectedPreviewIds.filter((x) => x !== id);
    },
    selectAllPreview() {
      this.selectedPreviewIds = this.previewRows.map((r) => r.id);
    },
    clearPreviewSelection() {
      this.selectedPreviewIds = [];
    },
    isOfferCollapsed(guid) {
      return !!this.offerCollapsedByGuid[guid];
    },
    toggleOfferNode(guid) {
      this.offerCollapsedByGuid = { ...this.offerCollapsedByGuid, [guid]: !this.offerCollapsedByGuid[guid] };
    },
    selectOfferTreeNode(node) {
      this.offerRuleDraft.leaf_guid = node.guid;
    },
    toggleOfferProductSelection(id) {
      const key = String(id);
      const current = this.offerRuleDraft.product_ids || [];
      if (current.includes(key)) {
        this.offerRuleDraft.product_ids = current.filter((x) => x !== key);
      } else {
        this.offerRuleDraft.product_ids = [...current, key];
      }
    },
    clearOfferTreeFilter() {
      this.offerTreeSearch = "";
      this.offerRuleDraft.leaf_guid = "";
    },
    clearOfferRuleFilters() {
      this.offerProductSearch = "";
      this.offerTreeSearch = "";
      this.offerRuleDraft.leaf_guid = "";
      this.offerRuleDraft.product_ids = [];
    },
    addOfferRule() {
      if (this.offerCampaignIsReadOnly) {
        this.$toast.error("Nuk mund të shtosh rregulla në ofertë të mbaruar.");
        return;
      }
      if (this.offerRuleDraft.scope === "tree" && !this.offerRuleDraft.leaf_guid) {
        this.$toast.error("Zgjidh nje kategori nga pema.");
        return;
      }
      if (this.offerRuleDraft.scope === "selected" && !(this.offerRuleDraft.product_ids || []).length) {
        this.$toast.error("Zgjidh te pakten nje produkt.");
        return;
      }
      const rule = {
        related_entity: this.offerRuleDraft.related_entity,
        scope: this.offerRuleDraft.scope,
        target_field: "price",
        discount_type: this.offerRuleDraft.discount_type,
        amount: Number(this.offerRuleDraft.amount || 0),
        leaf_guid: this.offerRuleDraft.scope === "tree" ? this.offerRuleDraft.leaf_guid : null,
        product_ids: this.offerRuleDraft.scope === "selected" ? [...this.offerRuleDraft.product_ids] : [],
      };
      this.offerRules = [...this.offerRules, rule];
      this.offerRuleDraft.amount = 0;
      this.offerRuleDraft.leaf_guid = "";
      this.offerRuleDraft.product_ids = [];
      this.offerProductSearch = "";
      this.offerTreeSearch = "";
    },
    removeOfferRule(idx) {
      if (this.offerCampaignIsReadOnly) return;
      this.offerRules = this.offerRules.filter((_, i) => i !== idx);
    },
    offerRuleScopeText(rule) {
      if (rule.scope === "tree") {
        const name = this.getOfferCategoryName(rule.related_entity, rule.leaf_guid);
        return name ? `Peme: ${name}` : "Peme (pa kategori)";
      }
      if (rule.scope === "selected") {
        return `${(rule.product_ids || []).length} produkte`;
      }
      return "Te gjitha";
    },
    offerEntityLabel(entity) {
      if (entity === "accessory" || entity === "accessories") return "Aksesore";
      return "Produkte";
    },
    offerScopeLabel(scope) {
      if (scope === "tree") return "Nga pema";
      if (scope === "selected") return "Te zgjedhura";
      return "Te gjitha";
    },
    offerTargetLabel(target) {
      void target;
      return "Nga bazë → cmim";
    },
    offerDiscountTypeLabel(type) {
      if (type === "flat") return "Ulje fikse";
      if (type === "fixed_price") return "Cmim i ri fiks";
      return "Ulje %";
    },
    offerStatusLabel(status) {
      if (status === "active") return "Aktive";
      if (status === "scheduled") return "Ne pritje";
      if (status === "completed") return "Mbaruar";
      if (status === "cancelled") return "Anuluar";
      if (status === "draft") return "Draft";
      if (status) return String(status);
      return "Draft";
    },
    getOfferCategoryName(entity, guid) {
      if (!guid) return "";
      const source = this.categoryTrees[this.normalizeOfferRuleEntity(entity)] || [];
      const stack = [...source];
      while (stack.length) {
        const n = stack.pop();
        if (n.guid === guid) return n.name || guid;
        if (Array.isArray(n.children) && n.children.length) {
          n.children.forEach((ch) => stack.push(ch));
        }
      }
      return guid;
    },
    normalizeOfferRuleEntity(relatedEntity) {
      const e = String(relatedEntity || "products").toLowerCase();
      if (e === "accessory" || e === "accessories") return "accessories";
      return "products";
    },
    getOfferCategoryPathLabels(relatedEntity, targetGuid) {
      if (!targetGuid) return [];
      const roots = this.categoryTrees[this.normalizeOfferRuleEntity(relatedEntity)] || [];
      const findPath = (nodes, guid, stack) => {
        for (let i = 0; i < nodes.length; i++) {
          const n = nodes[i];
          const next = [...stack, n.name || n.guid];
          if (n.guid === guid) return next;
          if (n.children?.length) {
            const hit = findPath(n.children, guid, next);
            if (hit) return hit;
          }
        }
        return null;
      };
      return findPath(roots, targetGuid, []) || [];
    },
    getOfferTreeBranchGuids(relatedEntity, leafGuid) {
      if (!leafGuid) return [];
      const roots = this.categoryTrees[this.normalizeOfferRuleEntity(relatedEntity)] || [];
      const stack = [...roots];
      let rootNode = null;
      while (stack.length) {
        const current = stack.pop();
        if (current.guid === leafGuid) {
          rootNode = current;
          break;
        }
        if (current.children?.length) {
          current.children.forEach((ch) => stack.push(ch));
        }
      }
      if (!rootNode) return [String(leafGuid)];
      const all = [];
      const walk = (node) => {
        all.push(String(node.guid));
        if (node.children?.length) {
          node.children.forEach((child) => walk(child));
        }
      };
      walk(rootNode);
      return all;
    },
    offerProductCategoryGuid(product, relatedEntity) {
      if (!product) return "";
      return this.normalizeOfferRuleEntity(relatedEntity) === "accessories"
        ? String(product.accessory_id || "")
        : String(product.type_id || "");
    },
    getOfferProductCategoryPathLabels(relatedEntity, product) {
      const guid = this.offerProductCategoryGuid(product, relatedEntity);
      return this.getOfferCategoryPathLabels(relatedEntity, guid);
    },
    sortOfferSimulationRows(rows) {
      return [...(rows || [])].sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), "sq"));
    },
    hydrateOfferDraft(item) {
      if (!item || !item.guid) return;
      this.offerLoadedCampaignStatus = this.effectiveOfferCampaignStatus(item);
      this.offerDraftGuid = item.guid;
      this.selectedOfferGuid = item.guid;
      if (item.fushat_guid) {
        this.offerFushatGuid = item.fushat_guid;
        this.selectedFushatGuid = item.fushat_guid;
        this.hydrateOfferFushatFormFromItem(item);
      }
      this.offerForm = {
        label: item?.label || "",
        start_at: this.normalizePickerDateTime(item?.start_at),
        end_at: this.normalizePickerDateTime(item?.end_at),
        priority: Number(item?.priority || 0),
        is_price_locked: Boolean(item?.is_price_locked),
      };
      this.offerRules = (item?.rules || []).map((r) => ({
        related_entity: r.related_entity || "products",
        scope: r.scope || "all",
        target_field: r.target_field || "price",
        discount_type: r.discount_type || "percentage",
        amount: Number(r.amount || 0),
        leaf_guid: r.leaf_guid || null,
        product_ids: Array.isArray(r.product_ids) ? r.product_ids.map((id) => String(id)) : [],
      }));
      this.offerImageFile = null;
      this.offerImagePreviewUrl = this.resolveOfferImageUrl(this.offerFushatForm.image);
      this.offerSyncedOk = Number(item.synced_ok || 0);
      this.offerSyncedFailed = Number(item.synced_failed || 0);
      this.offerSyncFailures = [];
      this.offerSyncDetailExpanded = false;
      this.loadOfferAudits();
      if (this.offerSyncedFailed > 0) {
        this.loadOfferSyncFailures({ silent: true });
      }
      this.syncOfferOfertaListKindFromLoadedStatus();
      this.setOfferViewModeFromStatus(this.offerLoadedCampaignStatus);
      this.offerSimulationRan = false;
      this.offerSimulationRows = [];
      this.offerSimulationAffected = 0;
    },
    hydrateOfferFushatFormFromItem(item) {
      const fushat = (this.offerFushatList || []).find((f) => f.guid === item.fushat_guid);
      this.offerFushatForm = {
        name: fushat?.name || item.fushat_name || item.name || "",
        code: fushat?.code || item.fushat_code || item.code || "",
        image: fushat?.image || item.fushat_image || item.image || "",
      };
      this.offerFushatImagePreviewUrl = this.resolveOfferImageUrl(this.offerFushatForm.image);
    },
    hydrateOfferFushat(fushat) {
      if (!fushat?.guid) return;
      this.offerFushatGuid = fushat.guid;
      this.selectedFushatGuid = fushat.guid;
      this.offerFushatForm = {
        name: fushat.name || "",
        code: fushat.code || "",
        image: fushat.image || "",
      };
      this.offerFushatImagePreviewUrl = this.resolveOfferImageUrl(fushat.image);
      this.offerFushatImageFile = null;
    },
    resetOfferFushatForm() {
      this.offerFushatGuid = "";
      this.selectedFushatGuid = "";
      this.offerFushatForm = { name: "", code: "", image: "" };
      this.offerFushatImageFile = null;
      this.offerFushatImagePreviewUrl = "";
    },
    resetOfferDraftForm() {
      this.offerLoadedCampaignStatus = "draft";
      this.offerOfertaListKind = "draft";
      this.offerDraftGuid = "";
      this.selectedOfferGuid = "";
      this.offerForm = { label: "", start_at: null, end_at: null, priority: 0, is_price_locked: false };
      this.applyDefaultNewCampaignDatesMidnight();
      this.offerRules = [];
      this.offerRuleDraft = {
        related_entity: "products",
        scope: "all",
        target_field: "price",
        discount_type: "percentage",
        amount: 0,
        leaf_guid: "",
        product_ids: [],
      };
      this.offerProductSearch = "";
      this.offerTreeSearch = "";
      this.offerImageFile = null;
      this.offerImagePreviewUrl = "";
      this.offerAudits = [];
      this.offerSyncedOk = 0;
      this.offerSyncedFailed = 0;
      this.offerSyncFailures = [];
      this.offerSyncDetailExpanded = false;
      this.offerSimulationRows = [];
      this.offerSimulationAffected = 0;
      this.offerSimulationRan = false;
      this.setOfferTimelineFilterToCurrentMonth();
      void this.loadOfferTimeline();
      this.offerViewMode = "hub";
      this.offerWizardStep = 1;
    },
    setOfferTimelineFilterToCurrentMonth() {
      const now = new Date();
      const y = now.getFullYear();
      const m0 = now.getMonth();
      const first = new Date(y, m0, 1);
      const last = new Date(y, m0 + 1, 0);
      const iso = (d) => {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${d.getFullYear()}-${mm}-${dd}`;
      };
      this.offerTimelineFilter.from = iso(first);
      this.offerTimelineFilter.to = iso(last);
    },
    applyDefaultNewCampaignDatesMidnight() {
      const now = new Date();
      const y = now.getFullYear();
      const m0 = now.getMonth();
      const pad = (n) => String(n).padStart(2, "0");
      const lastDay = new Date(y, m0 + 1, 0).getDate();
      const startStr = `${y}-${pad(m0 + 1)}-01 00:00:00`;
      const endStr = `${y}-${pad(m0 + 1)}-${pad(lastDay)} 00:00:00`;
      this.offerForm.start_at = this.normalizePickerDateTime(startStr);
      this.offerForm.end_at = this.normalizePickerDateTime(endStr);
    },
    createNewFushat() {
      this.resetOfferFushatForm();
      this.resetOfferDraftForm();
      this.offerSection = "fushata";
      this.offerFushatModalOpen = true;
    },
    createNewOfferCampaign() {
      return this.createNewFushat();
    },
    async createNewOferta() {
      if (!this.offerFushatGuid) {
        this.$toast.error("Zgjidh ose krijo një fushatë së pari.");
        return;
      }
      this.offerCreateOfertaLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/create-oferta", {
          fushat_guid: this.offerFushatGuid,
          label: "",
        });
        const item = res?.data?.item;
        if (!item?.guid) {
          this.$toast.error("Përgjigje e pavlefshme nga serveri.");
          return;
        }
        this.resetOfferDraftForm();
        this.hydrateOfferDraft(item);
        this.applyDefaultNewCampaignDatesMidnight();
        await this.loadOfferDrafts();
        this.offerViewMode = "wizard";
        this.offerWizardStep = 1;
        this.$toast.success(res?.data?.message || "Oferta e re u krijua. Vendos orarin.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u krijua oferta.");
      } finally {
        this.offerCreateOfertaLoading = false;
      }
    },
    setOfferFushataListKind(kind) {
      this.offerFushataListKind = kind;
    },
    setOfferOfertaListKind(kind) {
      this.offerOfertaListKind = kind;
      const allowed = new Set(this.offerOfertaPickerList.map((x) => x.guid));
      if (this.selectedOfferGuid && !allowed.has(this.selectedOfferGuid)) {
        this.selectedOfferGuid = "";
      }
    },
    effectiveOfferCampaignStatus(item) {
      if (!item) return "draft";
      const raw = item.status != null ? String(item.status).trim().toLowerCase() : "";
      if (["draft", "scheduled", "active", "completed", "cancelled"].includes(raw)) return raw;
      if (item.cancelled_at) return "cancelled";
      if (item.deactivated_at) return "completed";
      if (item.activated_at) return "active";
      if (item.published_at) return "scheduled";
      return "draft";
    },
    syncOfferFushataListKindFromLoadedStatus() {
      return this.syncOfferOfertaListKindFromLoadedStatus();
    },
    syncOfferOfertaListKindFromLoadedStatus() {
      if (!this.offerDraftGuid) return;
      const s = this.offerLoadedCampaignStatus;
      if (s === "completed" || s === "cancelled") this.offerOfertaListKind = "completed";
      else if (s === "active" || s === "scheduled") this.offerOfertaListKind = "live";
      else this.offerOfertaListKind = "draft";
    },
    openOfferCampaignDataModal() {
      if (!this.offerDraftGuid) return;
      this.offerOfertaModalOpen = true;
    },
    openOfferFushatModal() {
      this.offerFushatModalOpen = true;
    },
    closeOfferFushatModal() {
      this.offerFushatModalOpen = false;
    },
    closeOfferOfertaModal() {
      this.offerOfertaModalOpen = false;
    },
    async openOfferCampaignFromPicker() {
      if (!this.selectedOfferGuid) return;
      if (this.selectedOfferGuid !== this.offerDraftGuid) {
        const ok = await this.loadSelectedOfferDraft();
        if (!ok) return;
      } else {
        this.syncOfferOfertaListKindFromLoadedStatus();
      }
      this.offerOfertaModalOpen = true;
    },
    async loadSelectedFushat() {
      if (!this.selectedFushatGuid) return false;
      this.offerFushatLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/fushat/get", { guid: this.selectedFushatGuid });
        const item = res?.data?.item;
        if (!item?.guid) {
          this.$toast.error("Fushata nuk u gjet.");
          return false;
        }
        this.hydrateOfferFushat(item);
        this.$toast.success("Fushata u ngarkua.");
        return true;
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u ngarkua fushata.");
        return false;
      } finally {
        this.offerFushatLoading = false;
      }
    },
    async saveOfferFushat() {
      if (!this.offerFushatForm.name.trim() || !this.offerFushatForm.code.trim()) {
        this.$toast.error("Plotëso emrin dhe kodin e fushatës.");
        return false;
      }
      this.offerFushatSaving = true;
      try {
        const res = await Api(true).post("product/pricing-offers/fushat/save", {
          guid: this.offerFushatGuid || undefined,
          name: this.offerFushatForm.name.trim(),
          code: this.offerFushatForm.code.trim(),
          image: this.offerFushatForm.image || null,
        });
        const item = res?.data?.item;
        if (item?.guid) {
          this.offerFushatGuid = item.guid;
          this.selectedFushatGuid = item.guid;
          this.hydrateOfferFushat(item);
        }
        await this.loadOfferFushatList();
        this.$toast.success(res?.data?.message || "Fushata u ruajt.");
        return true;
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjatë ruajtjes së fushatës.");
        return false;
      } finally {
        this.offerFushatSaving = false;
      }
    },
    async saveOfferFushatFromModal() {
      const wasNew = !this.offerFushatGuid;
      const ok = await this.saveOfferFushat();
      if (!ok) return;
      this.closeOfferFushatModal();
      if (wasNew && this.offerFushatGuid) {
        this.$toast.info("Tani krijo një ofertë me orar dhe çmime.", { timeout: 6000 });
      }
    },
    closeOfferNewCampaignModal() {
      this.closeOfferOfertaModal();
    },
    async saveOfferDraftFromModal() {
      const ok = await this.saveOfferDraft();
      if (ok) this.closeOfferOfertaModal();
    },
    async copyOfferCampaignAsDraft(sourceGuid) {
      if (!sourceGuid) {
        this.$toast.error("Zgjidh ose ngarko një fushatë për ta kopjuar.");
        return;
      }
      this.offerCopyLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/copy-as-draft", { source_guid: sourceGuid });
        const item = res?.data?.item;
        if (!item?.guid) {
          this.$toast.error("Përgjigje e pavlefshme nga serveri.");
          return;
        }
        this.hydrateOfferDraft(item);
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        await this.loadOfferAudits();
        this.offerViewMode = "wizard";
        this.offerWizardStep = 1;
        this.offerOfertaListKind = "draft";
        this.$toast.success(res?.data?.message || "Oferta u kopjua si draft.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u kopjua fushata.");
      } finally {
        this.offerCopyLoading = false;
      }
    },
    async copyOfferCampaignFromPicker() {
      const sourceGuid = this.selectedOfferGuid || this.offerDraftGuid;
      await this.copyOfferCampaignAsDraft(sourceGuid);
    },
    copyLoadedOfferCampaignFromModal() {
      return this.copyOfferCampaignAsDraft(this.offerDraftGuid);
    },
    async resetTimelineToCurrentMonthAndReload() {
      this.setOfferTimelineFilterToCurrentMonth();
      await this.loadOfferTimeline();
    },
    async loadOfferFushatList() {
      try {
        const res = await Api(true).post("product/pricing-offers/fushat/list", { limit: 200 });
        this.offerFushatList = Array.isArray(res?.data?.items) ? res.data.items : [];
      } catch (e) {
        this.offerFushatList = [];
      }
    },
    async loadOfferDrafts() {
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/list", { limit: 300 });
        this.offerDrafts = Array.isArray(res?.data?.items) ? res.data.items : [];
      } catch (e) {
        this.offerDrafts = [];
      }
    },
    async loadOfferTimeline() {
      try {
        const payload = {
          from: this.offerTimelineFilter.from || undefined,
          to: this.offerTimelineFilter.to || undefined,
          limit: 300,
        };
        const res = await Api(true).post("product/pricing-offers/campaign/timeline", payload);
        this.offerTimeline = Array.isArray(res?.data?.items) ? res.data.items : [];
      } catch (e) {
        this.offerTimeline = [];
      }
    },
    async loadOfferHealth() {
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/health", {});
        this.offerHealth = res?.data || {};
      } catch (e) {
        this.offerHealth = {};
      }
    },
    async loadOfferAudits() {
      if (!this.offerDraftGuid) {
        this.offerAudits = [];
        return;
      }
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/audits", { guid: this.offerDraftGuid, limit: 100 });
        this.offerAudits = Array.isArray(res?.data?.items) ? res.data.items : [];
      } catch (e) {
        this.offerAudits = [];
      }
    },
    formatTimelinePeriod(startAt, endAt) {
      const from = startAt ? String(startAt).slice(0, 16).replace("T", " ") : "Pa fillim";
      const to = endAt ? String(endAt).slice(0, 16).replace("T", " ") : "Pa mbarim";
      return `${from} -> ${to}`;
    },
    async openTimelineCampaign(guid) {
      this.selectedOfferGuid = guid;
      await this.loadSelectedOfferDraft();
    },
    onTimelineBarClick(guid) {
      if (this.timelineDrag?.moved) return;
      this.openTimelineCampaign(guid);
    },
    startTimelineDrag(event, row, mode) {
      if (!this.canDragTimelineBar(row)) return;
      if (!this.offerTimelineDays.length) return;
      const timelineGrid = event.currentTarget?.closest(".timeline-grid");
      const gridWidth = timelineGrid?.getBoundingClientRect?.().width || 0;
      const dayWidth = gridWidth > 0 ? gridWidth / this.offerTimelineDays.length : 32;
      this.timelineDrag = {
        guid: row.guid,
        mode,
        startX: event.clientX,
        dayWidth: Math.max(16, dayWidth),
        originalStart: row.barStart,
        originalEnd: row.barEnd,
        moved: false,
      };
      window.addEventListener("mousemove", this.onTimelineDragMove);
      window.addEventListener("mouseup", this.onTimelineDragEnd);
    },
    canDragTimelineBar(row) {
      const rowStatus = this.effectiveOfferCampaignStatus(row);
      if (rowStatus === "completed" || rowStatus === "cancelled" || rowStatus === "draft") return false;
      return !!(row?.start_at && row?.end_at);
    },
    onTimelineDragMove(event) {
      if (!this.timelineDrag) return;
      const drag = this.timelineDrag;
      const dayCount = this.offerTimelineDays.length;
      if (!dayCount) return;
      const delta = Math.round((event.clientX - drag.startX) / drag.dayWidth);
      if (delta === 0) return;
      drag.moved = true;

      let barStart = drag.originalStart;
      let barEnd = drag.originalEnd;
      const minBarLen = 1;
      const originalLen = drag.originalEnd - drag.originalStart;

      if (drag.mode === "move") {
        barStart = drag.originalStart + delta;
        barEnd = drag.originalEnd + delta;
        if (barStart < 1) {
          const shift = 1 - barStart;
          barStart += shift;
          barEnd += shift;
        }
        if (barEnd > dayCount + 1) {
          const shift = barEnd - (dayCount + 1);
          barStart -= shift;
          barEnd -= shift;
        }
      } else if (drag.mode === "left") {
        barStart = Math.max(1, Math.min(drag.originalStart + delta, drag.originalEnd - minBarLen));
      } else if (drag.mode === "right") {
        barEnd = Math.min(dayCount + 1, Math.max(drag.originalEnd + delta, drag.originalStart + minBarLen));
      }

      if (drag.mode === "move" && barEnd - barStart !== originalLen) {
        barEnd = barStart + originalLen;
      }

      this.timelineBarOverrides = {
        ...this.timelineBarOverrides,
        [drag.guid]: { barStart, barEnd },
      };
    },
    async onTimelineDragEnd() {
      if (!this.timelineDrag) return;
      window.removeEventListener("mousemove", this.onTimelineDragMove);
      window.removeEventListener("mouseup", this.onTimelineDragEnd);

      const drag = this.timelineDrag;
      this.timelineDrag = null;
      if (!drag.moved) return;

      const row = this.offerTimelineRows.find((r) => r.guid === drag.guid);
      const override = this.timelineBarOverrides[drag.guid];
      if (!row || !override) return;
      if (override.barStart === drag.originalStart && override.barEnd === drag.originalEnd) {
        this.timelineBarOverrides = { ...this.timelineBarOverrides, [drag.guid]: null };
        return;
      }

      const startDay = this.offerTimelineDays[override.barStart - 1]?.key || null;
      const endDay = this.offerTimelineDays[Math.max(0, override.barEnd - 2)]?.key || null;
      if (!startDay || !endDay) return;

      const startAt = this.mergeDateWithTime(startDay, row.start_at, false);
      const endAt = this.mergeDateWithTime(endDay, row.end_at, true);

      try {
        await Api(true).post("product/pricing-offers/campaign/update-schedule", {
          guid: row.guid,
          start_at: startAt,
          end_at: endAt,
        });
        this.$toast.success("Orari i ofertes u perditesua.");
        await this.loadOfferTimeline();
        if (this.offerDraftGuid === row.guid) {
          await this.loadSelectedOfferDraft();
        }
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate perditesimit te orarit.");
        this.timelineBarOverrides = {};
      }
    },
    mergeDateWithTime(dateKey, oldDateTime, endDefault) {
      const old = oldDateTime ? String(oldDateTime) : "";
      const timeMatch = old.match(/\d{2}:\d{2}(:\d{2})?/);
      const time = timeMatch ? timeMatch[0] : endDefault ? "23:59:59" : "00:00:00";
      const normalized = time.length === 5 ? `${time}:00` : time;
      return `${dateKey} ${normalized}`;
    },
    formatDateTimeForApi(value) {
      if (!value) return null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const mon = String(value.getMonth() + 1).padStart(2, "0");
        const d = String(value.getDate()).padStart(2, "0");
        const h = String(value.getHours()).padStart(2, "0");
        const min = String(value.getMinutes()).padStart(2, "0");
        return `${y}-${mon}-${d} ${h}:${min}:00`;
      }

      if (typeof value === "string") {
        const raw = value.replace("T", " ").trim();
        const full = raw.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
        if (full) return `${full[1]} ${full[2]}:${full[3]}:${full[4]}`;
        const short = raw.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}):(\d{2})$/);
        if (short) return `${short[1]} ${short[2]}:${short[3]}:00`;
      }

      // Fallback for odd object values
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        const y = parsed.getFullYear();
        const mon = String(parsed.getMonth() + 1).padStart(2, "0");
        const d = String(parsed.getDate()).padStart(2, "0");
        const h = String(parsed.getHours()).padStart(2, "0");
        const min = String(parsed.getMinutes()).padStart(2, "0");
        return `${y}-${mon}-${d} ${h}:${min}:00`;
      }
      return null;
    },
    normalizePickerDateTime(value) {
      if (!value) return null;
      const raw = String(value).trim();
      const m1 = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}):(\d{2})(?::\d{2})?/);
      if (m1) return `${m1[1]} ${m1[2]}:${m1[3]}:00`;
      return null;
    },
    /** Normalize picker value to `yyyy-MM-dd HH:mm:ss` for comparisons and coercion. */
    stringifyOfferPickerValue(val) {
      if (val == null || val === "") return null;
      if (typeof val === "string") {
        return this.normalizePickerDateTime(val);
      }
      if (val instanceof Date && !Number.isNaN(val.getTime())) {
        return this.formatDateTimeForApi(val);
      }
      return null;
    },
    /**
     * When the calendar date changes (or first pick), use 00:00:00. When only the time changes on the same day, keep the new time.
     */
    coerceOfferSchedulePickerValue(newVal, previousVal) {
      const newStr = this.stringifyOfferPickerValue(newVal);
      if (!newStr) return null;
      const prevStr = this.stringifyOfferPickerValue(previousVal);
      const newDate = newStr.slice(0, 10);
      if (!prevStr || prevStr.slice(0, 10) !== newDate) {
        return `${newDate} 00:00:00`;
      }
      return newStr;
    },
    onOfferScheduleStartUpdate(val) {
      this.offerForm.start_at = this.coerceOfferSchedulePickerValue(val, this.offerForm.start_at);
    },
    onOfferScheduleEndUpdate(val) {
      this.offerForm.end_at = this.coerceOfferSchedulePickerValue(val, this.offerForm.end_at);
    },
    onOfferImagePicked(e) {
      return this.onOfferFushatImagePicked(e);
    },
    onOfferFushatImagePicked(e) {
      const file = e?.target?.files?.[0];
      this.offerFushatImageFile = file || null;
      if (!file) {
        this.offerFushatImagePreviewUrl = this.resolveOfferImageUrl(this.offerFushatForm.image);
        return;
      }
      this.offerFushatImagePreviewUrl = URL.createObjectURL(file);
    },
    resolveOfferImageUrl(path) {
      if (!path) return "";
      if (/^https?:\/\//i.test(path)) return path;
      return path.startsWith("/") ? path : `/${path}`;
    },
    async uploadOfferImage() {
      return this.uploadOfferFushatImage();
    },
    async uploadOfferFushatImage() {
      if (!this.offerFushatGuid) {
        const ok = await this.saveOfferFushat();
        if (!ok || !this.offerFushatGuid) {
          this.$toast.error("Ruaj fushatën para ngarkimit të imazhit.");
          return;
        }
      }
      if (!this.offerFushatImageFile) {
        this.$toast.error("Zgjidh një imazh.");
        return;
      }
      this.offerFushatImageUploading = true;
      try {
        const fd = new FormData();
        fd.append("guid", this.offerFushatGuid);
        fd.append("image", this.offerFushatImageFile);
        const res = await Api(true).post("product/pricing-offers/fushat/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const item = res?.data?.item;
        if (item?.guid) this.hydrateOfferFushat(item);
        await this.loadOfferFushatList();
        this.offerFushatImageFile = null;
        this.offerFushatImagePreviewUrl = this.resolveOfferImageUrl(this.offerFushatForm.image);
        this.$toast.success(res?.data?.message || "Imazhi u ngarkua.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjatë ngarkimit të imazhit.");
      } finally {
        this.offerFushatImageUploading = false;
      }
    },
    async loadSelectedOfferDraft() {
      if (!this.selectedOfferGuid) return false;
      this.offerDraftLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/get", { guid: this.selectedOfferGuid });
        const item = res?.data?.item || null;
        if (!item?.guid) {
          this.$toast.error("Përgjigje e pavlefshme nga serveri.");
          return false;
        }
        this.hydrateOfferDraft(item);
        this.$toast.success("Oferta u ngarkua.");
        return true;
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u ngarkua oferta.");
        return false;
      } finally {
        this.offerDraftLoading = false;
      }
    },
    buildOfferSavePayload() {
      return {
        guid: this.offerDraftGuid || undefined,
        fushat_guid: this.offerFushatGuid,
        label: (this.offerForm.label || "").trim() || null,
        start_at: this.formatDateTimeForApi(this.offerForm.start_at),
        end_at: this.formatDateTimeForApi(this.offerForm.end_at),
        priority: Number(this.offerForm.priority || 0),
        is_price_locked: Boolean(this.offerForm.is_price_locked),
        target_field: "price",
        rules: this.offerRules.map((r) => ({
          related_entity: r.related_entity,
          scope: r.scope,
          target_field: "price",
          discount_type: r.discount_type,
          amount: Number(r.amount || 0),
          leaf_guid: r.scope === "tree" ? r.leaf_guid : null,
          product_ids: r.scope === "selected" ? (r.product_ids || []).map((id) => Number(id)) : [],
        })),
      };
    },
    async saveOfferDraft(options = {}) {
      const silent = Boolean(options?.silent);
      if (this.offerCampaignIsReadOnly) {
        if (!silent) this.$toast.error("Kjo ofertë është e mbaruar — nuk ruhet.");
        return false;
      }
      if (!this.offerFushatGuid) {
        if (!silent) this.$toast.error("Zgjidh ose krijo një fushatë.");
        return false;
      }
      this.offerDraftSaving = true;
      try {
        const payload = this.buildOfferSavePayload();
        const endpoint = this.offerCampaignIsDraft
          ? "product/pricing-offers/campaign/save-draft"
          : "product/pricing-offers/campaign/update-campaign";
        const res = await Api(true).post(endpoint, payload);
        const savedItem = res?.data?.item || null;
        if (silent) {
          if (savedItem?.guid) {
            this.offerDraftGuid = savedItem.guid;
            this.selectedOfferGuid = savedItem.guid;
          }
        } else {
          this.hydrateOfferDraft(savedItem);
        }
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        await this.loadOfferAudits();
        if (!silent) {
          const extra = res?.data?.result;
          const syncFail = financaBulkFailureText(extra?.failed_sync);
          const syncNote = extra
            ? (syncFail
              ? ` ${syncFail}`
              : (Number(extra.synced_ok || 0) > 0 ? ` ${extra.synced_ok} u ruajtën në Financa 1 dhe Financa 2.` : ""))
            : "";
          const saveText = (res?.data?.message || "U ruajt.") + syncNote;
          if (syncFail) this.$toast.warning(saveText);
          else this.$toast.success(saveText);
        }
        return true;
      } catch (e) {
        if (!silent) this.$toast.error(e?.response?.data?.message || "Gabim gjate ruajtjes.");
        return false;
      } finally {
        this.offerDraftSaving = false;
      }
    },
    async previewOfferImpact() {
      if (!this.offerRules.length) {
        this.$toast.error("Shto te pakten nje rregull para parashikimit.");
        return;
      }
      if (this.offerHasIncompleteTreeRule) {
        this.$toast.error("Ke rregull peme pa kategori.");
        return;
      }
      await this.simulateOfferDraft({ announce: true });
    },
    async publishOfferDraft(skipConfirm) {
      if (!skipConfirm) {
        this.requestPublishOfferDraft();
        return;
      }
      if (!this.offerRules.length) {
        this.$toast.error("Shto te pakten nje rregull para publikimit.");
        return;
      }
      if (this.offerHasIncompleteTreeRule) {
        this.$toast.error("Ke rregull peme pa kategori.");
        return;
      }
      const ok = await this.saveOfferDraft({ silent: true });
      if (!ok || !this.offerDraftGuid) {
        this.$toast.error("Ruaje draft-in para publikimit.");
        return;
      }
      this.offerPublishLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/publish", { guid: this.offerDraftGuid });
        if (res?.data?.item) this.hydrateOfferDraft(res.data.item);
        this.applyOfferSyncCountsFromItemOrResult(res?.data?.item, res?.data?.result);
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        await this.loadOfferAudits();
        await this.loadOfferHealth();
        if (Number(this.offerSyncedFailed || 0) > 0) {
          await this.loadOfferSyncFailures({ silent: true });
        }
        this.setOfferViewModeFromStatus(this.offerLoadedCampaignStatus);
        this.$toast.success(res?.data?.message || "Oferta u publikua.");
        this.maybeOpenOfferSyncErrorModal(res?.data?.result, "oferta_apply");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate publikimit.");
      } finally {
        this.offerPublishLoading = false;
      }
    },
    async simulateOfferDraft(options = {}) {
      const announce = Boolean(options?.announce);
      if (!this.offerCampaignIsReadOnly) {
        const ok = await this.saveOfferDraft({ silent: true });
        if (!ok || !this.offerDraftGuid) {
          this.$toast.error("Ruaje draft-in per te pare simulimin.");
          return;
        }
      } else if (!this.offerDraftGuid) {
        this.$toast.error("Ngarko një ofertë për simulim.");
        return;
      }
      this.offerSimulationLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/simulate", { guid: this.offerDraftGuid, limit: 250 });
        this.offerSimulationRan = true;
        this.offerSimulationRows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
        this.offerSimulationAffected = Number(res?.data?.affected || this.offerSimulationRows.length);
        if (this.offerSimulationAffected > 0) {
          this.$toast.success(`Simulim i gjetur: ${this.offerSimulationAffected} produkte preken.`);
        } else if (announce) {
          this.$toast.success("Simulimi u krye: nuk ka ndryshim cmimi per rregullat aktuale.");
        }
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate simulimit.");
      } finally {
        this.offerSimulationLoading = false;
      }
    },
    async processOffersNow() {
      this.offerProcessNowLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/process-now", {});
        const r = res?.data?.result || {};
        this.offerProcessResult = r;
        this.$toast.success(
          `U aktivizuan ${r.activated || 0} oferta, u mbyllën ${r.completed || 0}, u përditësuan çmimet për ${r.updated || 0} produkte.`,
        );
        await this.loadOfferHealth();
        await this.loadOfferTimeline();
        await this.loadOfferDrafts();
        this.maybeOpenOfferSyncErrorModal(r, "oferta_apply");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate process now.");
      } finally {
        this.offerProcessNowLoading = false;
      }
    },
    async rollbackOfferDraft(skipConfirm) {
      if (!this.offerDraftGuid) return;
      if (!skipConfirm) {
        this.requestRollbackOfferDraft();
        return;
      }
      this.offerRollbackLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/rollback", { guid: this.offerDraftGuid });
        if (res?.data?.item) this.hydrateOfferDraft(res.data.item);
        this.applyOfferSyncCountsFromItemOrResult(res?.data?.item, res?.data?.result);
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        await this.loadOfferAudits();
        if (Number(this.offerSyncedFailed || 0) > 0) {
          await this.loadOfferSyncFailures({ silent: true });
        }
        this.setOfferViewModeFromStatus(this.offerLoadedCampaignStatus);
        this.$toast.success(res?.data?.message || "Rollback u krye.");
        this.maybeOpenOfferSyncErrorModal(res?.data?.result, "oferta_apply");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Gabim gjate rollback.");
      } finally {
        this.offerRollbackLoading = false;
      }
    },
    async deleteSelectedOfferDraft(skipConfirm) {
      const guid = this.selectedOfferGuid || this.offerDraftGuid;
      if (!guid) return;
      if (!skipConfirm) {
        this.requestDeleteSelectedOfferDraft();
        return;
      }
      this.offerDeleteLoading = true;
      try {
        await Api(true).post("product/pricing-offers/campaign/delete", { guid });
        if (this.offerDraftGuid === guid) {
          this.resetOfferDraftForm();
        }
        if (this.selectedOfferGuid === guid) {
          this.selectedOfferGuid = "";
        }
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        this.$toast.success("Drafti u fshi.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u fshi drafti.");
      } finally {
        this.offerDeleteLoading = false;
      }
    },
    async deleteLoadedFushat(skipConfirm) {
      if (!this.offerFushatGuid) return;
      if (!skipConfirm) {
        this.requestDeleteLoadedFushat();
        return;
      }
      this.offerFushatDeleteLoading = true;
      try {
        await Api(true).post("product/pricing-offers/fushat/delete", { guid: this.offerFushatGuid });
        this.resetOfferFushatForm();
        this.resetOfferDraftForm();
        this.selectedFushatGuid = "";
        await this.loadOfferFushatList();
        await this.loadOfferDrafts();
        await this.loadOfferTimeline();
        this.$toast.success("Fushata u fshi.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u fshi fushata.");
      } finally {
        this.offerFushatDeleteLoading = false;
      }
    },
    async downloadPriceImportTemplate() {
      try {
        const targetField = this.priceImportTargetField === "default_price" ? "default_price" : "price";
        const res = await Api(true).get("product/pricing-offers/import/template", {
          params: { target_field: targetField },
          responseType: "blob",
        });
        this.saveBlobDownload(res, targetField === "default_price" ? "cmim_baze_template.csv" : "cmim_template.csv");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u shkarkua shablloni.");
      }
    },
    async downloadOfferImportTemplate() {
      try {
        const res = await Api(true).get("product/pricing-offers/import/template", {
          params: { target_field: "offer" },
          responseType: "blob",
        });
        this.saveBlobDownload(res, "cmim_oferte_template.csv");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u shkarkua shablloni.");
      }
    },
    saveBlobDownload(res, fallbackName) {
      let filename = fallbackName;
      const disposition = res?.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      if (match && match[1]) filename = match[1];
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    async downloadOfferExport() {
      if (!this.offerDraftGuid) {
        this.$toast.error("Ngarko ose krijo një ofertë.");
        return;
      }
      if (!this.offerCampaignIsReadOnly) {
        const ok = await this.saveOfferDraft({ silent: true });
        if (!ok) return;
      }
      this.offerExportLoading = true;
      try {
        const res = await Api(true).post(
          "product/pricing-offers/campaign/export",
          { guid: this.offerDraftGuid },
          { responseType: "blob" },
        );
        this.saveBlobDownload(res, "oferta_eksport.xls");
        this.$toast.success("Lista e ofertës u shkarkua.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u shkarkua lista e ofertës.");
      } finally {
        this.offerExportLoading = false;
      }
    },
    async downloadFushatExport() {
      if (!this.offerFushatGuid) {
        this.$toast.error("Ngarko një fushatë së pari.");
        return;
      }
      this.offerFushatExportLoading = true;
      try {
        const res = await Api(true).post(
          "product/pricing-offers/fushat/export",
          { fushat_guid: this.offerFushatGuid },
          { responseType: "blob" },
        );
        this.saveBlobDownload(res, "fushata_ofertat.xls");
        this.$toast.success("Excel me të gjitha ofertat u shkarkua.");
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u shkarkua eksporti i fushatës.");
      } finally {
        this.offerFushatExportLoading = false;
      }
    },
    async previewPriceImport(file) {
      if (!file) return;
      this.priceImportLoading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("target_field", this.priceImportTargetField === "default_price" ? "default_price" : "price");
        const res = await Api(true).post("product/pricing-offers/import/preview", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.priceImportPreview = res?.data || null;
        const errorCount = Number(this.priceImportPreview?.error_count || 0);
        if (errorCount > 0) {
          const errors = this.previewRowsToImportErrors(this.priceImportPreview.rows);
          const payload = {
            source: "cmimet_preview",
            message: `${errorCount} rreshta kanë gabime. Rreshtat e vlefshëm mund të aplikohen.`,
            errors,
          };
          this.openImportErrorModal({
            title: "Gabime në skedarin CSV",
            ...payload,
            payload,
          });
          this.$toast.error(`${errorCount} rreshta me gabime.`);
        }
      } catch (e) {
        this.priceImportPreview = null;
        const data = this.extractApiErrorPayload(e);
        const message = data.message || "Gabim gjatë leximit të skedarit.";
        const errors = this.flattenValidationErrors(data.errors);
        const payload = { source: "cmimet_preview", message, errors, response: data };
        this.openImportErrorModal({
          title: "Gabim gjatë leximit të skedarit",
          message,
          source: "cmimet_preview",
          errors,
          payload,
        });
        this.$toast.error(message);
      } finally {
        this.priceImportLoading = false;
      }
    },
    onPriceImportFilePicked(e) {
      const file = e?.target?.files?.[0];
      this.priceImportFile = file || null;
      this.priceImportApplyResult = null;
      if (file) this.previewPriceImport(file);
    },
    async applyPriceImportContinue(timing) {
      const rows = this.priceImportValidRows;
      if (!rows.length) {
        this.$toast.error("Nuk ka rreshta të vlefshme për import.");
        return;
      }
      if (timing?.applyAt) {
        await this.schedulePriceImport(timing.applyAt);
        return;
      }

      const targetField = this.priceImportTargetField === "default_price" ? "default_price" : "price";
      this.priceImportApplying = true;
      this.priceImportApplyResult = null;
      this.priceImportProgress = { done: 0, total: rows.length, phase: "live" };
      let batchId = null;

      const applyChunks = async (erpTarget) => {
        const chunkSize = 5;
        let updated = 0;
        let targeted = 0;
        let synced_ok = 0;
        let synced_failed = 0;
        const failed_sync = [];
        this.priceImportProgress = { done: 0, total: rows.length, phase: erpTarget };

        for (let i = 0; i < rows.length; i += chunkSize) {
          const chunk = rows.slice(i, i + chunkSize);
          const product_rules = chunk.map((row) => ({
            product_id: Number(row.product_id),
            discount_type: "fixed_price",
            amount: Number(row.new_price != null ? row.new_price : row.cmim_oferte || 0),
          }));
          const file_base_prices = {};
          chunk.forEach((row) => {
            if (row.file_base_price != null && row.file_base_price !== "") {
              file_base_prices[Number(row.product_id)] = Number(row.file_base_price);
            }
          });

          let res;
          try {
            res = await this.postPricingApplyWithRetry({
              mode: "custom",
              related_entity: "products",
              target_field: targetField,
              scope: "selected",
              product_ids: chunk.map((r) => Number(r.product_id)),
              product_rules,
              batch_id: batchId,
              file_base_prices,
              erp_target: erpTarget,
            });
          } catch (e) {
            const network = this.isNetworkTimeoutError(e);
            if (!this.isTransientDbError(e) && !network) {
              throw e;
            }
            const reason = network
              ? "Lidhja u ndërpre (timeout)"
              : this.extractApiErrorPayload(e).message || "Connection refused";
            chunk.forEach((row) => {
              failed_sync.push({
                product_id: Number(row.product_id),
                code: row.code,
                name: row.name,
                erp: erpTarget,
                status: null,
                method: null,
                reason,
              });
            });
            this.priceImportProgress = {
              done: Math.min(rows.length, i + chunk.length),
              total: rows.length,
              phase: erpTarget,
            };
            if (network) {
              for (let j = i + chunkSize; j < rows.length; j++) {
                const row = rows[j];
                failed_sync.push({
                  product_id: Number(row.product_id),
                  code: row.code,
                  name: row.name,
                  erp: erpTarget,
                  status: null,
                  method: null,
                  reason: `Anuluar: ${financaServerLabel(erpTarget)} timeout në chunk-un e mëparshëm`,
                });
              }
              this.priceImportProgress = { done: rows.length, total: rows.length, phase: erpTarget };
              break;
            }
            continue;
          }

          targeted += Number(res.data.targeted ?? 0);
          updated += Number(res.data.updated || 0);
          synced_ok += Number(res.data.synced_ok || 0);
          synced_failed += Number(res.data.synced_failed || 0);
          if (Array.isArray(res.data.failed_sync)) {
            failed_sync.push(...res.data.failed_sync);
          }

          this.priceImportProgress = {
            done: Math.min(rows.length, i + chunk.length),
            total: rows.length,
            phase: erpTarget,
          };
        }

        return { updated, targeted, synced_ok, synced_failed, failed_sync };
      };

      try {
        const createRes = await Api(true).post("product/pricing-offers/price-batches", {
          source: "import",
          filename: this.priceImportFile?.name || null,
          target_field: targetField,
          product_count: rows.length,
          status: "applying",
        });
        batchId = createRes?.data?.batch?.id;
        if (!batchId) {
          throw new Error("Batch audit nuk u krijua.");
        }

        const liveRes = await applyChunks("live");
        const backupRes = await applyChunks("backup");
        const failed_sync = [...(liveRes.failed_sync || []), ...(backupRes.failed_sync || [])];
        const failedIds = new Set(failed_sync.map((f) => Number(f.product_id)).filter(Boolean));
        const targeted = liveRes.targeted;
        const updated = liveRes.updated;
        const synced_failed = failedIds.size;
        const synced_ok = Math.max(0, targeted - synced_failed);

        const finalStatus = this.priceBatchFinalStatus({ updated, synced_ok });
        await Api(true).post("product/pricing-offers/price-batches/finalize", {
          batch_id: batchId,
          status: finalStatus,
        });

        this.priceImportApplyResult = { targeted, updated, synced_ok, synced_failed, failed_sync };
        const importNote = synced_failed > 0
          ? (financaBulkFailureText(failed_sync) || `${synced_failed} nuk u ruajtën në Financa 1 ose Financa 2.`)
          : (synced_ok > 0 ? `${synced_ok} u ruajtën në Financa 1 dhe Financa 2.` : "");
        const importText = `Import: përditësuar ${updated}.${importNote ? ` ${importNote}` : ""}`;
        if (synced_failed > 0) this.$toast.warning(importText);
        else this.$toast.success(importText);
        if (failed_sync.length) {
          const payload = {
            source: "cmimet_apply",
            message: financaBulkFailureText(failed_sync) || `${synced_failed} nuk u ruajtën në Financa 1 ose Financa 2.`,
            errors: this.failedSyncToImportErrors(failed_sync),
          };
          this.openImportErrorModal({
            title: "Dështime sinkronizimi Financa 1 / Financa 2",
            ...payload,
            payload,
          });
        }
        this.allProducts = await ProductServices.getProducts();
        await this.loadPriceBatches();
      } catch (e) {
        await this.finalizePriceBatchStatus(batchId, "failed");
        const data = this.extractApiErrorPayload(e);
        const isNetwork = (e?.message || data.message || "") === "Network Error";
        const message = isNetwork
          ? "Lidhja u ndërpre (timeout). Kontrolloni auditin: cmimet mund të jenë ruajtur."
          : data.message || e?.message || "Gabim gjate importit te cmimeve.";
        const errors = this.flattenValidationErrors(data.errors);
        const payload = { source: "cmimet_apply", message, errors, response: data };
        this.openImportErrorModal({
          title: "Gabim gjatë importit të cmimeve",
          message,
          source: "cmimet_apply",
          errors,
          payload,
        });
        this.$toast.error(message);
        await this.loadPriceBatches();
      } finally {
        this.priceImportApplying = false;
      }
    },
    canCancelScheduledPriceBatch(batch) {
      return Boolean(batch?.id) && (batch.status || "") === "scheduled";
    },
    canApplyScheduledPriceBatchNow(batch) {
      return Boolean(batch?.id) && (batch.status || "") === "scheduled";
    },
    priceBatchActionBusy() {
      return !!(this.priceBatchExportId || this.priceBatchRevertId || this.priceBatchCancelId || this.priceBatchApplyNowId || this.priceBatchResyncId);
    },
    async schedulePriceImport(applyAt) {
      const rows = this.priceImportValidRows;
      if (!rows.length) {
        this.$toast.error("Nuk ka rreshta të vlefshme për import.");
        throw new Error("Nuk ka rreshta të vlefshme për import.");
      }
      try {
        const items = rows.map((row) => ({
          product_id: Number(row.product_id),
          code: row.code || null,
          new_price: Number(row.new_price != null ? row.new_price : row.cmim_oferte || 0),
          old_price: row.old_price != null && row.old_price !== "" ? Number(row.old_price) : undefined,
          file_base_price: row.file_base_price != null && row.file_base_price !== "" ? Number(row.file_base_price) : undefined,
        }));
        const batches = await this.postScheduledChunks({
          source: "import",
          filename: this.priceImportFile?.name || "import",
          target_field: this.priceImportTargetField === "default_price" ? "default_price" : "price",
          apply_at: applyAt,
          items,
        });
        const when = this.formatLocalDateTime(batches[0]?.apply_at || applyAt);
        if (batches.length > 1) {
          this.$toast.success(`U planifikua në ${batches.length} batch-e (max ${PRICE_SCHEDULE_ITEM_CAP} secili) për ${when}.`);
        } else {
          this.$toast.success(`U planifikua për ${when}.`);
        }
        await this.loadPriceBatches();
      } catch (e) {
        const message = this.apiErrorMessage(e, "Nuk u planifikua importi i cmimeve.");
        this.$toast.error(message);
        throw e;
      }
    },
    async scheduleManualDiscount(applyAt) {
      const selected = new Set(this.selectedPreviewIds.map((id) => Number(id)));
      const rows = (this.previewRows || []).filter((r) => selected.has(Number(r.id)));
      if (!rows.length) {
        this.$toast.error("Nuk ka produkte per perditesim.");
        throw new Error("Nuk ka produkte per perditesim.");
      }
      try {
        let baseName = "manual";
        if (this.form.scope === "tree") {
          const leaf = (this.visibleTreeNodes || []).find((n) => n.guid === this.form.leaf_guid);
          baseName = leaf?.name || "pema";
        } else if (this.form.scope === "all") {
          baseName = "te-gjitha";
        } else if (this.form.scope === "selected") {
          baseName = "te-zgjedhura";
        }
        const items = rows.map((row) => ({
          product_id: Number(row.id),
          code: row.code || null,
          new_price: Number(row.new_price || 0),
          old_price: row.base_value != null ? Number(row.base_value) : undefined,
        }));
        const batches = await this.postScheduledChunks({
          source: "manual",
          filename: baseName,
          target_field: this.form.target_field === "default_price" ? "default_price" : "price",
          apply_at: applyAt,
          items,
        });
        const when = this.formatLocalDateTime(batches[0]?.apply_at || applyAt);
        if (batches.length > 1) {
          this.$toast.success(`U planifikua në ${batches.length} batch-e (max ${PRICE_SCHEDULE_ITEM_CAP} secili) për ${when}.`);
        } else {
          this.$toast.success(`U planifikua për ${when}.`);
        }
        this.clearCustomRules();
        this.refreshPreview();
        await this.loadPriceBatches();
      } catch (e) {
        const message = this.apiErrorMessage(e, "Nuk u planifikua ndryshimi i cmimeve.");
        this.$toast.error(message);
        throw e;
      }
    },
    requestCancelScheduledPriceBatch(batch) {
      if (!this.canCancelScheduledPriceBatch(batch)) return;
      this.openConfirmModal({
        title: "Anulo ndryshimin e planifikuar",
        message: `Anulo batch #${batch.id}? Çmimet nuk do të ndryshohen.`,
        confirmLabel: "Anulo planifikimin",
        danger: true,
        action: () => this.cancelScheduledPriceBatch(batch),
      });
    },
    async cancelScheduledPriceBatch(batch) {
      if (!batch?.id) return;
      this.priceBatchCancelId = batch.id;
      try {
        await Api(true).post("product/pricing-offers/price-batches/cancel", { batch_id: batch.id });
        this.$toast.success("Planifikimi u anulua.");
        await this.loadPriceBatches();
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u anulua planifikimi.");
        throw e;
      } finally {
        this.priceBatchCancelId = null;
      }
    },
    requestApplyScheduledPriceBatchNow(batch) {
      if (!this.canApplyScheduledPriceBatchNow(batch)) return;
      this.openConfirmModal({
        title: "Apliko tani",
        message: `Apliko tani batch #${batch.id} (${batch.product_count} produkte) pa pritur orarin?`,
        warning: "Çmimet shkojnë në sistem dhe sinkronizohen në Financa 1 dhe Financa 2.",
        confirmLabel: "Apliko tani",
        action: () => this.applyScheduledPriceBatchNow(batch),
      });
    },
    async applyScheduledPriceBatchNow(batch) {
      if (!batch?.id) return;
      this.priceBatchApplyNowId = batch.id;
      try {
        const res = await Api(true).post("product/pricing-offers/price-batches/apply-now", { batch_id: batch.id });
        const updated = Number(res?.data?.updated || 0);
        const syncedOk = Number(res?.data?.synced_ok || 0);
        const syncedFailed = Number(res?.data?.synced_failed || 0);
        const applyNowNote = syncedFailed > 0
          ? (financaBulkFailureText(res?.data?.failed_sync) || `${syncedFailed} nuk u ruajtën në Financa 1 ose Financa 2.`)
          : (syncedOk > 0 ? `${syncedOk} u ruajtën në Financa 1 dhe Financa 2.` : "");
        const applyNowText = `Aplikuar: ${updated}.${applyNowNote ? ` ${applyNowNote}` : ""}`;
        if (syncedFailed > 0) this.$toast.warning(applyNowText);
        else this.$toast.success(applyNowText);
        this.allProducts = await ProductServices.getProducts();
        delete this.priceBatchDetails[batch.id];
        this.priceBatchDetails = { ...this.priceBatchDetails };
        await this.loadPriceBatches();
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u aplikua batch-i.");
        throw e;
      } finally {
        this.priceBatchApplyNowId = null;
      }
    },
    async loadPriceBatches() {
      this.priceBatchLoading = true;
      try {
        const payload = { limit: 100 };
        const search = (this.priceBatchFilterSearch || "").trim();
        if (search) payload.search = search;
        if (this.priceBatchFilterStatus) payload.status = this.priceBatchFilterStatus;
        const res = await Api(true).post("product/pricing-offers/price-batches/list", payload);
        this.priceBatches = res?.data?.batches || [];
      } catch (e) {
        this.priceBatches = [];
        this.$toast.error(e?.response?.data?.message || "Nuk u ngarkua audit i cmimeve.");
      } finally {
        this.priceBatchLoading = false;
      }
    },
    async togglePriceBatchDetail(batchId) {
      if (this.expandedPriceBatchId === batchId) {
        this.expandedPriceBatchId = null;
        return;
      }
      this.expandedPriceBatchId = batchId;
      if (this.priceBatchDetails[batchId]) return;
      this.priceBatchDetailLoading = batchId;
      try {
        const res = await Api(true).post("product/pricing-offers/price-batches/get", { batch_id: batchId });
        this.priceBatchDetails = {
          ...this.priceBatchDetails,
          [batchId]: res?.data?.batch || { items: [] },
        };
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u ngarkuan detajet e batch-it.");
      } finally {
        this.priceBatchDetailLoading = null;
      }
    },
    async downloadPriceBatchCsv(batch) {
      if (!batch?.id) return;
      this.priceBatchExportId = batch.id;
      try {
        const res = await Api(true).get("product/pricing-offers/price-batches/export", {
          params: { batch_id: batch.id },
          responseType: "blob",
        });
        this.saveBlobDownload(res, `price_batch_${batch.id}_audit.csv`);
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u shkarkua CSV i auditit.");
      } finally {
        this.priceBatchExportId = null;
      }
    },
    async revertPriceBatch(batch, skipConfirm) {
      if (!batch?.id) return;
      if ((batch.target_field || "price") === "default_price") {
        this.$toast.error("Ndryshimet e cmimit baze nuk mund te rikthehen.");
        return;
      }
      if (!skipConfirm) {
        this.requestRevertPriceBatch(batch);
        return;
      }

      this.priceBatchRevertId = batch.id;
      try {
        let detail = this.priceBatchDetails[batch.id];
        if (!detail?.items) {
          const getRes = await Api(true).post("product/pricing-offers/price-batches/get", { batch_id: batch.id });
          detail = getRes?.data?.batch || { items: [] };
          this.priceBatchDetails = { ...this.priceBatchDetails, [batch.id]: detail };
        }

        const productIds = (detail.items || []).map((i) => Number(i.product_id)).filter(Boolean);
        const chunkSize = 35;
        let updated = 0;
        let synced_ok = 0;
        let synced_failed = 0;
        const failed_sync = [];

        if (!productIds.length) {
          await Api(true).post("product/pricing-offers/price-batches/revert", {
            batch_id: batch.id,
            mark_reverted: true,
          });
        } else {
          for (let i = 0; i < productIds.length; i += chunkSize) {
            const chunk = productIds.slice(i, i + chunkSize);
            const isLast = i + chunkSize >= productIds.length;
            const res = await Api(true).post("product/pricing-offers/price-batches/revert", {
              batch_id: batch.id,
              product_ids: chunk,
              mark_reverted: isLast,
            });
            updated += Number(res?.data?.updated || 0);
            synced_ok += Number(res?.data?.synced_ok || 0);
            synced_failed += Number(res?.data?.synced_failed || 0);
            if (Array.isArray(res?.data?.failed_sync)) failed_sync.push(...res.data.failed_sync);
          }
        }

        const revertNote = synced_failed > 0
          ? (financaBulkFailureText(failed_sync) || `${synced_failed} nuk u ruajtën në Financa 1 ose Financa 2.`)
          : (synced_ok > 0 ? `${synced_ok} u ruajtën në Financa 1 dhe Financa 2.` : "");
        const revertText = `Rikthyer: ${updated}.${revertNote ? ` ${revertNote}` : ""}`;
        if (synced_failed > 0) this.$toast.warning(revertText);
        else this.$toast.success(revertText);
        this.allProducts = await ProductServices.getProducts();
        delete this.priceBatchDetails[batch.id];
        this.priceBatchDetails = { ...this.priceBatchDetails };
        if (this.expandedPriceBatchId === batch.id) {
          this.expandedPriceBatchId = null;
        }
        await this.loadPriceBatches();
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u rikthyen cmimet.");
      } finally {
        this.priceBatchRevertId = null;
      }
    },
    async resyncPriceBatchFailed(batch) {
      if (!batch?.id) return;
      this.priceBatchResyncId = batch.id;
      try {
        let detail = this.priceBatchDetails[batch.id];
        if (!detail?.items) {
          const getRes = await Api(true).post("product/pricing-offers/price-batches/get", { batch_id: batch.id });
          detail = getRes?.data?.batch || { items: [] };
          this.priceBatchDetails = { ...this.priceBatchDetails, [batch.id]: detail };
        }

        const productIds = (detail.items || [])
          .filter((i) => !i.synced_ok)
          .map((i) => Number(i.product_id))
          .filter(Boolean);
        const chunkSize = 5;
        const failed_sync = [];
        let attempted = 0;
        let syncedOk = Number(batch.synced_ok || 0);
        let syncedFailed = Number(batch.synced_failed || 0);

        if (!productIds.length) {
          const res = await Api(true).post("product/pricing-offers/price-batches/resync-failed", {
            batch_id: batch.id,
          });
          syncedOk = Number(res?.data?.synced_ok ?? syncedOk);
          syncedFailed = Number(res?.data?.synced_failed ?? syncedFailed);
        } else {
          for (let i = 0; i < productIds.length; i += chunkSize) {
            const chunk = productIds.slice(i, i + chunkSize);
            const res = await Api(true).post("product/pricing-offers/price-batches/resync-failed", {
              batch_id: batch.id,
              product_ids: chunk,
            });
            attempted += Number(res?.data?.attempted || chunk.length);
            syncedOk = Number(res?.data?.synced_ok ?? syncedOk);
            syncedFailed = Number(res?.data?.synced_failed ?? syncedFailed);
            if (Array.isArray(res?.data?.failed_sync)) {
              failed_sync.push(...res.data.failed_sync);
            }
          }
        }

        const retryNote = syncedFailed > 0
          ? (financaBulkFailureText(failed_sync) || `${syncedFailed} nuk u ruajtën në Financa 1 ose Financa 2.`)
          : (syncedOk > 0 ? `${syncedOk} u ruajtën në Financa 1 dhe Financa 2.` : "");
        const retryText = `Riprovuar: ${attempted}.${retryNote ? ` ${retryNote}` : ""}`;
        if (syncedFailed > 0) this.$toast.warning(retryText);
        else this.$toast.success(retryText);
        if (failed_sync.length && syncedFailed > 0) {
          const payload = {
            source: "cmimet_resync",
            message: financaBulkFailureText(failed_sync) || `${syncedFailed} nuk u ruajtën në Financa 1 ose Financa 2.`,
            errors: this.failedSyncToImportErrors(failed_sync),
          };
          this.openImportErrorModal({
            title: "Dështime sinkronizimi Financa 1 / Financa 2",
            ...payload,
            payload,
          });
        }
        this.allProducts = await ProductServices.getProducts();
        delete this.priceBatchDetails[batch.id];
        this.priceBatchDetails = { ...this.priceBatchDetails };
        if (this.expandedPriceBatchId === batch.id) {
          this.expandedPriceBatchId = null;
        }
        await this.loadPriceBatches();
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Nuk u riprovua sinkronizimi F5.");
      } finally {
        this.priceBatchResyncId = null;
      }
    },
    async previewOfferImport(file) {
      if (!file) return;
      this.offerImportLoading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await Api(true).post("product/pricing-offers/import/preview", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.offerImportPreview = res?.data || null;
        const errorCount = Number(this.offerImportPreview?.error_count || 0);
        if (errorCount > 0) {
          const errors = this.previewRowsToImportErrors(this.offerImportPreview.rows);
          const payload = {
            source: "oferta_preview",
            message: `${errorCount} rreshta kanë gabime. Rreshtat e vlefshëm mund të shtohen si rregulla.`,
            errors,
          };
          this.openImportErrorModal({
            title: "Gabime në skedarin CSV",
            ...payload,
            payload,
          });
          this.$toast.error(`${errorCount} rreshta me gabime.`);
        }
      } catch (e) {
        this.offerImportPreview = null;
        const data = this.extractApiErrorPayload(e);
        const message = data.message || "Gabim gjatë leximit të skedarit.";
        const errors = this.flattenValidationErrors(data.errors);
        const payload = { source: "oferta_preview", message, errors, response: data };
        this.openImportErrorModal({
          title: "Gabim gjatë leximit të skedarit",
          message,
          source: "oferta_preview",
          errors,
          payload,
        });
        this.$toast.error(message);
      } finally {
        this.offerImportLoading = false;
      }
    },
    onOfferImportFilePicked(e) {
      const file = e?.target?.files?.[0];
      this.offerImportFile = file || null;
      if (file) this.previewOfferImport(file);
    },
    async applyOfferImportAsRules() {
      if (!this.offerDraftGuid) {
        this.$toast.error("Ruaj ose ngarko një ofertë së pari.");
        return;
      }
      if (this.offerCampaignIsReadOnly) {
        this.$toast.error("Ofertat e mbaruara nuk importohen.");
        return;
      }
      const rows = this.offerImportValidRows;
      if (!rows.length) {
        this.$toast.error("Nuk ka rreshta të vlefshme për import.");
        return;
      }
      this.offerImportLoading = true;
      try {
        const res = await Api(true).post("product/pricing-offers/campaign/import-rules", {
          guid: this.offerDraftGuid,
          replace: true,
          rows,
        });
        const item = res?.data?.item;
        if (item) this.hydrateOfferDraft(item);
        const extra = res?.data?.result;
        const failedSync = extra?.failed_sync || [];
        const ruleFail = financaBulkFailureText(failedSync);
        const syncNote = ruleFail
          ? ` ${ruleFail}`
          : (extra && Number(extra.synced_ok || 0) > 0 ? ` ${extra.synced_ok} u ruajtën në Financa 1 dhe Financa 2.` : "");
        const ruleText = (res?.data?.message || "Rregullat u importuan.") + syncNote;
        if (failedSync.length) this.$toast.warning(ruleText);
        else this.$toast.success(ruleText);
        if (failedSync.length) {
          const payload = {
            source: "oferta_apply",
            message: ruleFail || `${failedSync.length} nuk u ruajtën në Financa 1 ose Financa 2.`,
            errors: this.failedSyncToImportErrors(failedSync),
          };
          this.openImportErrorModal({
            title: "Dështime sinkronizimi Financa 1 / Financa 2",
            ...payload,
            payload,
          });
        }
        this.offerImportPreview = null;
        this.offerImportFile = null;
        await this.loadOfferTimeline();
      } catch (e) {
        const data = this.extractApiErrorPayload(e);
        const message = data.message || "Gabim gjatë importit të rregullave.";
        const errors = this.flattenValidationErrors(data.errors);
        const payload = { source: "oferta_apply", message, errors, response: data };
        this.openImportErrorModal({
          title: "Gabim gjatë importit të rregullave",
          message,
          source: "oferta_apply",
          errors,
          payload,
        });
        this.$toast.error(message);
      } finally {
        this.offerImportLoading = false;
      }
    },
  },
  watch: {
    productSearch() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedProductSearch = this.productSearch;
        this.customPage = 1;
      }, 180);
    },
    customTotalPages() {
      if (this.customPage > this.customTotalPages) {
        this.customPage = this.customTotalPages;
      }
    },
    "form.related_entity"() {
      this.form.leaf_guid = "";
      this.form.selected_product_ids = [];
      this.previewRows = [];
      this.treeSearch = "";
      this.productSearch = "";
      this.debouncedProductSearch = "";
      this.customRules = {};
      this.customPage = 1;
    },
    "form.leaf_guid"() {
      this.customPage = 1;
    },
  },
  async created() {
    const screen = this.$options.pricingScreen || this.screen || this.pricingScreen || "cmimet";
    this.pricingScreen = screen;
    const permissionByScreen = {
      cmimet: "can_see_cmimet",
      oferta: "can_see_oferta",
      kalendar: "can_see_calendar",
    };
    const needed = permissionByScreen[screen] || "can_see_cmimet";
    const allowed =
      this.am_allowed("pricing." + needed) || this.am_allowed("menu_roles.can_see_catalog_pricing");
    if (!allowed) {
      window.location.href = "/home";
      return;
    }

    const routeNameByScreen = {
      cmimet: "cmimet",
      oferta: "cmimet-oferta",
      kalendar: "cmimet-kalendar",
    };
    if (this.$root?.setCurrentRoute) {
      this.$root.setCurrentRoute(routeNameByScreen[screen] || "cmimet");
    }

    if (screen === "kalendar") {
      this.setOfferTimelineFilterToCurrentMonth();
      return;
    }

    this.allProducts = await ProductServices.getProducts();
    const [productCategories, accessoryCategories] = await Promise.all([
      CategoryServices.getCategories("products"),
      CategoryServices.getCategories("accessories"),
    ]);
    this.categoryTrees.products = await CategoryServices.getTreeCategories(productCategories, "id");
    this.categoryTrees.accessories = await CategoryServices.getTreeCategories(accessoryCategories, "id");

    if (screen === "cmimet") {
      await this.loadPriceBatches();
      return;
    }

    if (screen === "oferta") {
      this.activeTab = "oferta";
      this.setOfferTimelineFilterToCurrentMonth();
      await this.loadOfferFushatList();
      await this.loadOfferDrafts();
      await this.loadOfferTimeline();
      await this.loadOfferHealth();
    }
  },
  beforeUnmount() {
    window.removeEventListener("mousemove", this.onTimelineDragMove);
    window.removeEventListener("mouseup", this.onTimelineDragEnd);
  },
};
